// ============================================================================
// PREMIUM THEMED TABLE SVG - EXACT Premium STYLE
// ============================================================================
// Premium-style poker table with dealer at top, wider horizontal shape,
// seats positioned around the outside, matching reference image exactly
// ============================================================================

import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Gift, MessageCircle, Users } from 'lucide-react';
import { TipDealerButton } from './TipDealerModal';
import GameItemsMenu from './GameItemsMenu';
import PlayerActionAnimation from './PlayerActionAnimation';
import GameResultNotification from './GameResultNotification';
import WinnerCelebration from './WinnerCelebration';
import BuyInModal from './BuyInModal';
import TableChat from './TableChat';
import { subscribeToMessages, ChatMessage } from '../services/chatService';
import TableRulesModal from './TableRulesModal';
import UserSearch from './UserSearch';
import ShareWinModal from './stories/ShareWinModal';
import gameItemsService from '../services/gameItemsService';
import gameApiService from '../services/gameApiService';
import gameEngine from '../services/gameEngine';
import * as tableService from '../services/tableService';
import soundService from '../services/soundService';
import walletService from '../services/walletService';
import { ActiveAnimation } from '../types/gameItemsTypes';
import voiceAnnouncer from '../utils/SofiaVoiceAnnouncer';
import { triggerHaptic } from '../utils/iosUtils';
import { getDefaultAvatarUrl } from '../utils/defaultAvatar';
import RabbitHuntModal from './RabbitHuntModal';
import DealAnimation from './DealAnimation';
import './PremiumThemedTableSVG.css';

import { pssBustForBot, stripEmoji } from '../utils/pssBots';

// Still in the hand for the reveal: not folded, OR busted on this very hand
// (the server stamps a bust folded+needsRebuy at hand end; the cards were live).
const inShowdownHand = (p: any): boolean =>
  !!p && (!p.folded || ((p.needsRebuy || p.eliminated) && Array.isArray(p.holeCards) && p.holeCards.length === 2 && !!p.holeCards[0]?.rank));

// Premium Mode: ONLY win sounds + Sofia voice - nothing else
const playActionSound = (action: string, enabled: boolean) => {
  if (!enabled) return;
  // ONLY win sound plays - Sofia handles all other announcements
  if (action === 'win') {
    soundService.play(action);
  }
};

interface Card {
  rank: string;
  suit: 'hearts' | 'diamonds' | 'clubs' | 'spades';
}

interface Player {
  id: string;
  name: string;
  chips: number;
  currentBet?: number;
  folded?: boolean;
  allIn?: boolean;
  eliminated?: boolean;
  seat: number;
  holeCards?: Card[];
  waitingForNextHand?: boolean;
  needsRebuy?: boolean;
  isReady?: boolean;
  photoURL?: string;
  lastAction?: string;
}

interface HandResult {
  winnerId: string;
  winnerName?: string;
  winningHand?: { type: string; cards?: Card[] };
  potWon: number;
  allOthersFolded?: boolean;
  winnerShowedCards?: boolean;
}

interface PremiumThemedTableSVGProps {
  gameState: {
    players: Player[];
    communityCards: Card[];
    pot: number;
    currentPlayerIndex: number;
    round: string;
    status: string;
    smallBlind: number;
    bigBlind: number;
    dealerButton?: number;
    dealerIndex?: number;
    winners?: { playerId: string; amount: number; hand?: string }[];
    handResult?: HandResult | null;
    currency?: 'playcoins' | 'pkcash';
    lastRaiseAmount?: number;
    handId?: string;
    stateVersion?: number;
    handNumber?: number;
    turnStartedAt?: number;
    allInOccurred?: boolean;
    allInShowdown?: boolean;
    showdownComplete?: boolean;
    // Run It Twice
    runItTwiceOffer?: {
      active: boolean;
      players?: string[];
      responses: { [playerId: string]: boolean | undefined };
    };
    runItTwiceResult?: any;
    // Bomb Pot
    isBombPot?: boolean;
    bombPotAnte?: number;
    // Straddle
    straddleAmount?: number;
    straddlePlayerId?: string;
    // 7-2 Game
    sevenTwoWinner?: { playerId: string; bounty: number };
  };
  currentUserId: string;
  tableId: string;
  usernames?: { [uid: string]: string };
  userPhotos?: { [uid: string]: string | undefined };
  onSeatClick?: (seatIndex: number) => void;
  /** Tap a seated player to open their profile sheet (VPIP, tags, career). */
  onPlayerClick?: (playerId: string, playerName: string) => void;
  voiceEnabled?: boolean;
  soundEnabled?: boolean;
  tableName?: string;
  maxSeats?: number;
  dealerImageUrl?: string;
  onTipDealer?: () => void;
  // Seats held by players sitting out (dealt out, no gameState row):
  // rendered dimmed with a SITTING OUT tag so the seat visibly stays theirs
  awaySeated?: Array<{ id: string; name: string }>;
  tableCurrency?: 'playcoins' | 'pkcash';
  sessionMode?: boolean; // home game: free join, host banks chips
  specialRules?: {
    allInOrFold?: boolean;
    bombPotEnabled?: boolean;
    bombPotAnte?: number;
    sevenTwoGame?: boolean;
    sevenTwoBounty?: number;
    straddleEnabled?: boolean;
    straddleMin?: number;
    straddleMax?: number;
    runItTwiceEnabled?: boolean;
  };
  // Game items support
  onPlayerPositionsUpdate?: (positions: { [id: string]: { x: number; y: number } }) => void;
  onReload?: () => void;
  // Tournament header overlay (CoinPoker-style on-table info strip).
  // When present, the table renders a top-of-felt strip with name, level,
  // blinds, next-level countdown, and players remaining.
  tournamentInfo?: {
    name?: string;
    currentBlindLevel?: number;
    smallBlind?: number;
    bigBlind?: number;
    ante?: number;
    levelStartedAtMs?: number;     // when current blind level started
    levelDurationMin?: number;     // duration of current level in minutes
    playersRemaining?: number;
    totalPlayers?: number;
    currency?: 'playcoins' | 'pkcash';
    // Prize ladder (CoinPoker-style felt info): what busting now pays vs.
    // the next rung up. Both derive from the live pool incl. re-entries.
    nextPrize?: number;
    payJump?: number;
    prizePool?: number;
  };
  // Tournament felt info lines drawn inside the table SVG
  centerInfo?: string[];
}

const PremiumThemedTableSVG: React.FC<PremiumThemedTableSVGProps> = ({
  gameState,
  currentUserId,
  tableId,
  usernames = {},
  userPhotos = {},
  onSeatClick,
  onPlayerClick,
  voiceEnabled = true,
  soundEnabled = true,
  tableName = 'Poker Table',
  awaySeated = [],
  maxSeats = 8,
  dealerImageUrl = '/images/dealers/dealer-female-1.png',
  onTipDealer,
  tableCurrency = 'playcoins',
  sessionMode = false,
  specialRules,
  onPlayerPositionsUpdate,
  onReload,
  tournamentInfo,
  centerInfo,
}) => {
  // Extract state
  const rawPlayers = gameState?.players || [];
  const players = rawPlayers.filter((p: any) => p && p.id);
  const communityCards = gameState?.communityCards || [];
  const pot = gameState?.pot || 0;
  const currentPlayerIndex = gameState?.currentPlayerIndex ?? -1;
  const round = gameState?.round || 'waiting';
  const handResult = gameState?.handResult || null;
  const winners = gameState?.winners || [];

  // ── PORTRAIT MODE ─────────────────────────────────────────────────────
  // Phones get a dedicated tall racetrack geometry (CoinPoker-style);
  // desktop keeps the wide horizontal table untouched. The flag drives the
  // viewBox, felt shape, seat anchors, and card sizes below.
  // matchMedia, not innerWidth: it's the exact mechanism the stylesheet
  // media queries use, so the JS geometry and the CSS chrome can never
  // disagree (DevTools device emulation fires 'change' reliably where a
  // window resize event may never come).
  const [isPortrait, setIsPortrait] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = () => setIsPortrait(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange); // older iOS WebKit
    window.addEventListener('resize', onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);

  // Premium-style dimensions - WIDE horizontal table (desktop) or tall
  // portrait racetrack (phones).
  const SVG_WIDTH = isPortrait ? 720 : 1100;
  const SVG_HEIGHT = isPortrait ? 1180 : 650;
  const CENTER_X = SVG_WIDTH / 2;
  const CENTER_Y = isPortrait ? 520 : SVG_HEIGHT / 2 + 50;

  // Wide table proportions like Premium (desktop only - portrait draws a
  // rounded-rect racetrack from fixed coordinates instead of these radii)
  const TABLE_RX = 420; // Very wide
  const TABLE_RY = 180; // Shorter height
  const RAIL_WIDTH = 22;

  // Seats outside the rail (desktop ellipse ring)
  const SEATS_RADIUS_X = TABLE_RX + 90;
  const SEATS_RADIUS_Y = TABLE_RY + 110;

  // Portrait: the dealer artwork rises above y=0, so the viewBox grows
  // headroom only on tables that actually have a dealer.
  const portraitHasDealer = isPortrait && !!dealerImageUrl;
  const VIEWBOX = isPortrait
    ? `0 ${portraitHasDealer ? -190 : -100} 720 ${portraitHasDealer ? 1370 : 1290}`
    : `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`;

  // Portrait seat anchors - clockwise from the hero at bottom center.
  // Every anchor sits ON the rail centerline of the racetrack (a stadium:
  // caps centered (360,364)/(360,676) r~317, straights x=80/x=640), so
  // players straddle the table edge like a real seat instead of floating
  // on the felt. Plates hang outward into the background.
  const PORTRAIT_SEATS: { [k: number]: Array<[number, number]> } = {
    2: [[360, 985], [360, 58]],
    3: [[360, 985], [585, 141], [135, 141]],
    4: [[360, 985], [640, 520], [360, 58], [80, 520]],
    5: [[360, 985], [600, 880], [585, 141], [135, 141], [120, 880]],
    6: [[360, 985], [585, 899], [640, 400], [360, 58], [80, 400], [135, 899]],
    7: [[360, 985], [585, 899], [640, 470], [585, 141], [135, 141], [80, 470], [135, 899]],
    8: [[360, 985], [585, 899], [640, 540], [585, 141], [360, 58], [135, 141], [80, 540], [135, 899]],
    9: [[360, 985], [560, 920], [645, 600], [640, 340], [500, 80], [220, 80], [80, 340], [75, 600], [160, 920]],
    10: [[360, 985], [560, 920], [640, 620], [640, 420], [500, 80], [360, 58], [220, 80], [80, 420], [80, 620], [160, 920]],
  };

  // ============================================================================
  // REFS - For tracking state across renders
  // ============================================================================
  const gameStateRef = useRef(gameState);
  const prevRoundRef = useRef<string | null>(null);
  const prevWinnerRef = useRef<string | null>(null);
  const prevPlayerIndexRef = useRef<number | null>(null);
  const prevActionsRef = useRef<{ [playerId: string]: string }>({});
  const lastFetchedHandRef = useRef<number | null>(null);
  const lastHandForTimerRef = useRef<number | null>(null);
  // Throttle key for /kickStalledTurn pings so the client doesn't spam the
  // server while a stuck bot is being recovered. Format: `${tableId}:${turnStartedAt}:${attempt}`.
  const lastKickRef = useRef<{ key: string; at: number } | null>(null);
  const lastRunoutHandRef = useRef<number | null>(null);
  const lastPlayingRoundRef = useRef<string>('preflop');
  const lastResolvedHandRef = useRef<number>(0);
  const turnTimerRef = useRef<NodeJS.Timeout | null>(null);
  const runoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const runoutActiveRef = useRef<boolean>(false);
  const wasMyTurnRef = useRef<boolean>(false); // Track if it was already my turn

  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);

  // State
  const [hoveredSeat, setHoveredSeat] = useState<number | null>(null);
  const [dealerLoaded, setDealerLoaded] = useState(false);
  const [dealerError, setDealerError] = useState(false);

  // Ticking "now" for the on-table tournament-level countdown. 1s resolution
  // is plenty for a mm:ss readout and avoids re-rendering the whole table
  // every animation frame.
  const [nowTick, setNowTick] = useState<number>(Date.now());
  useEffect(() => {
    if (!tournamentInfo?.levelStartedAtMs || !tournamentInfo?.levelDurationMin) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [tournamentInfo?.levelStartedAtMs, tournamentInfo?.levelDurationMin]);
  
  // Game items state
  const [activeAnimations, setActiveAnimations] = useState<ActiveAnimation[]>([]);
  const [playerPositions, setPlayerPositions] = useState<{ [id: string]: { x: number; y: number } }>({});
  const [showItemsMenu, setShowItemsMenu] = useState(false);
  const [pkCashBalance, setPkCashBalance] = useState(0);

  // ============================================================================
  // TURN TIMER STATE
  // ============================================================================
  const TURN_DURATION = 30;
  const [turnTimeLeft, setTurnTimeLeft] = useState<number>(TURN_DURATION);
  const [timeBankUsed, setTimeBankUsed] = useState(false);

  // ============================================================================
  // RUNOUT STATE - All-in showdown animation
  // ============================================================================
  type RunoutPhase = 'none' | 'reveal-cards' | 'show-equity' | 'deal-turn' | 'deal-river' | 'complete';
  const [runoutPhase, setRunoutPhase] = useState<RunoutPhase>('none');
  const [revealedPlayerIndex, setRevealedPlayerIndex] = useState<number>(-1);
  const [revealedCardIndex, setRevealedCardIndex] = useState<number>(-1);
  const [visibleBoardCount, setVisibleBoardCount] = useState<number>(0);
  const [playerEquities, setPlayerEquities] = useState<{ [playerId: string]: number }>({});
  const [cachedPot, setCachedPot] = useState<number>(0);

  // ============================================================================
  // WINNER/NOTIFICATION STATE
  // ============================================================================
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState<any>(null);
  const [lastWinnerNotificationId, setLastWinnerNotificationId] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAmount, setCelebrationAmount] = useState(0);
  const [lastCelebrationHandId, setLastCelebrationHandId] = useState<string | null>(null);
  const [nextHandCountdown, setNextHandCountdown] = useState<number>(5);

  // ============================================================================
  // REBUY STATE
  // ============================================================================
  const [showRebuyModal, setShowRebuyModal] = useState(false);
  const [isRebuyProcessing, setIsRebuyProcessing] = useState(false);

  //  Rabbit hunt modal

  const [showRabbitModal, setShowRabbitModal] = useState(false);

  // ============================================================================
  // SHOW CARDS STATE (Fold Wins)
  // ============================================================================
  const [showCardsChosen, setShowCardsChosen] = useState(false);
  const [showCardsTimer, setShowCardsTimer] = useState(5);
  const [showCardsExpired, setShowCardsExpired] = useState(false);

  // ============================================================================
  // RUN IT TWICE STATE
  // ============================================================================
  type RitPhase = 'none' | 'reveal-cards' | 'equity-before' | 'run1-flop' | 'run1-turn' | 'run1-river' | 'run1-winner' | 'run2-flop' | 'run2-turn' | 'run2-river' | 'run2-winner' | 'complete';
  const [ritPhase, setRitPhase] = useState<RitPhase>('none');
  // Run It Multiple Times: boards render ON the felt for everyone, revealed
  // one run at a time. runs[] is canonical; legacy run1/run2 mapped in.
  const ritRuns: any[] | null = (() => {
    const rr = (gameState as any).runItTwiceResults;
    if (!rr) return null;
    if (Array.isArray(rr.runs) && rr.runs.length >= 2) return rr.runs;
    if (rr.run1 && rr.run2) return [rr.run1, rr.run2];
    return null;
  })();
  const [ritRunsVisible, setRitRunsVisible] = useState(0);
  // Cards visible of the run CURRENTLY revealing (shared prefix included).
  // Runs below ritRunsVisible are locked in with their winner labels.
  const [ritPartialCards, setRitPartialCards] = useState(0);
  const ritRevealActive = !!ritRuns && ritRunsVisible < (ritRuns?.length || 0);
  useEffect(() => {
    if (!ritRuns) { setRitRunsVisible(0); setRitPartialCards(0); return; }

    // Shared board prefix = the cards that were already on the felt at the
    // all-in (identical across runs). Derived from the boards themselves so
    // a missing server field can never break the reveal.
    const b0 = ritRuns[0]?.communityCards || [];
    const b1 = ritRuns[1]?.communityCards || [];
    let shared = 0;
    while (shared < b0.length && shared < b1.length
      && b0[shared]?.rank === b1[shared]?.rank && b0[shared]?.suit === b1[shared]?.suit) shared++;
    const totalCards = b0.length || 5;

    const heroPlayers = safePlayers
      .filter(p => p && inShowdownHand(p) && Array.isArray(p.holeCards) && p.holeCards.length === 2
        && p.holeCards.every((c: any) => c && c.rank))
      .map(p => ({ id: p.id, holeCards: p.holeCards }));

    // Live percentages per street, RIT-correct: cards burned onto EARLIER
    // runs' boards are DEAD for later runs (one deck, no reshuffle), so
    // they are excluded from every simulation.
    const runEquity = async (runIdx: number, cards: number) => {
      if (heroPlayers.length < 2) return;
      try {
        const { calculateEquity } = await import('../services/equityCalculator');
        const board = (ritRuns[runIdx]?.communityCards || []).slice(0, cards);
        const dead: any[] = [];
        for (let r = 0; r < runIdx; r++) {
          dead.push(...(ritRuns[r]?.communityCards || []).slice(shared));
        }
        const res = calculateEquity(heroPlayers as any, board, 600, dead);
        const map: { [id: string]: number } = {};
        res.forEach((r: any) => { map[r.playerId] = r.equity; });
        setPlayerEquities(map);
      } catch { /* equity is garnish - the reveal continues without it */ }
    };

    // Timed reveal: per run - shared prefix + equity, then each street
    // with suspense and recalculated percentages, then the winner label,
    // then the next run starts.
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    let t = 300;
    setRitRunsVisible(0);
    setRitPartialCards(0);
    for (let r = 0; r < ritRuns.length; r++) {
      const rr = r;
      timers.push(setTimeout(() => {
        setRitRunsVisible(rr);
        setRitPartialCards(shared);
        runEquity(rr, shared);
      }, t));
      t += 1500;
      // Real poker: the flop lands as three cards, then the turn, then the
      // river, each with a pause and fresh percentages.
      const streets: number[] = [];
      if (shared < 3) streets.push(3);
      if (shared < 4) streets.push(4);
      if (shared < 5) streets.push(Math.min(5, totalCards));
      for (const cc of streets) {
        timers.push(setTimeout(() => {
          setRitPartialCards(cc);
          runEquity(rr, cc);
        }, t));
        t += cc >= totalCards ? 2400 : 2100;
      }
      timers.push(setTimeout(() => {
        setRitRunsVisible(rr + 1);
        setRitPartialCards(0);
        if (rr + 1 >= ritRuns.length) setPlayerEquities({});
      }, t));
      t += 1400;
    }
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!ritRuns, gameState.handNumber]);
  const [showRITModal, setShowRITModal] = useState(false);
  const [ritResponding, setRitResponding] = useState(false);
  const ritTimerRef = useRef<NodeJS.Timeout | null>(null);
  const ritOfferRef = useRef<string | null>(null);
  const lastRitHandRef = useRef<number | null>(null);

  // ============================================================================
  // STRADDLE STATE
  // ============================================================================
  const [canStraddle, setCanStraddle] = useState(false);
  const [straddlePosted, setStraddlePosted] = useState(false);
  const [postingStraddle, setPostingStraddle] = useState(false);

  // ============================================================================
  // BOMB POT STATE
  // ============================================================================
  const [showBombPotAnnouncement, setShowBombPotAnnouncement] = useState(false);
  const lastBombPotHandRef = useRef<number | null>(null);

  // ============================================================================
  // 7-2 BOUNTY STATE
  // ============================================================================
  const [showSevenTwoBounty, setShowSevenTwoBounty] = useState(false);
  const [sevenTwoBountyAmount, setSevenTwoBountyAmount] = useState(0);
  const [sevenTwoBountyWinner, setSevenTwoBountyWinner] = useState('');
  const lastSevenTwoHandRef = useRef<number | null>(null);

  // ============================================================================
  // CHAT STATE
  // ============================================================================
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // ============================================================================
  // BOT CHAT BUBBLE STATE - Shows Kevin Hart / Sophia messages on table
  // ============================================================================
  const [botChatBubble, setBotChatBubble] = useState<string | null>(null);
  const [botChatBubbleName, setBotChatBubbleName] = useState<string>('');
  const [botChatBubbleSenderId, setBotChatBubbleSenderId] = useState<string | null>(null);

  // ⚡ PRE-ACTIONS: armed while waiting for the turn (CoinPoker-style)
  const [preAction, setPreAction] = useState<'checkFold' | 'check' | 'callAny' | null>(null);
  const botChatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastBotMessageIdRef = useRef<string | null>(null);

  // ============================================================================
  // ROUND ANNOUNCEMENT STATE
  // ============================================================================
  const [showRoundAnnouncement, setShowRoundAnnouncement] = useState(false);
  const [roundAnnouncementText, setRoundAnnouncementText] = useState('');

  // ============================================================================
  // MODALS STATE (Invite, Rules)
  // ============================================================================
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);

  // ============================================================================
  // SHARE WIN STATE (Stories)
  // ============================================================================
  const [showShareWinModal, setShowShareWinModal] = useState(false);
  const [shareWinData, setShareWinData] = useState<{
    potAmount: number;
    handType?: string;
    opponents?: { odD: string; username: string; hand?: string; folded?: boolean }[];
    wasShowdown?: boolean;
  } | null>(null);
  const [showShareWinButton, setShowShareWinButton] = useState(false);

  // ============================================================================
  // SECURE HOLE CARDS STATE
  // ============================================================================
  const [myHoleCards, setMyHoleCards] = useState<{rank: string; suit: string}[]>([]);

  // ============================================================================
  // BOT CHAT BUBBLE EFFECT - Subscribe to bot messages and show as speech bubble
  // ============================================================================
  useEffect(() => {
    if (!tableId) return;

    // Check if there's a bot at the table
    const hasBotPlayer = players.some((p: any) => p?.id?.startsWith('house_bot_'));
    if (!hasBotPlayer) return;

    const unsubscribe = subscribeToMessages(tableId, (messages: ChatMessage[]) => {
      if (messages.length === 0) return;

      // Find the latest bot message
      const latestBotMsg = [...messages].reverse().find(
        (m) => m.senderId?.startsWith('house_bot_') || (m as any).isHouseBot
      );

      if (!latestBotMsg || latestBotMsg.id === lastBotMessageIdRef.current) return;

      lastBotMessageIdRef.current = latestBotMsg.id;
      setBotChatBubble(stripEmoji(latestBotMsg.message || ''));
      setBotChatBubbleName(latestBotMsg.senderName || 'House Bot');
      setBotChatBubbleSenderId(latestBotMsg.senderId || null);

      // Clear previous timer
      if (botChatTimerRef.current) clearTimeout(botChatTimerRef.current);

      // Auto-hide after 4.5s - the pssBubbleLife animation fades it out
      // over the same window so it never blinks off abruptly.
      botChatTimerRef.current = setTimeout(() => {
        setBotChatBubble(null);
      }, 4500);
    }, 10); // Only need recent messages

    return () => {
      unsubscribe();
      if (botChatTimerRef.current) clearTimeout(botChatTimerRef.current);
    };
  }, [tableId, players]);

  // Game state checks
  const isShowdown = round === 'showdown' || round === 'ended';
  const isFoldWin = handResult?.allOthersFolded === true;
  const isRunoutInProgress = runoutPhase !== 'none' && runoutPhase !== 'complete';

  // User state - merge secure hole cards
  const safePlayers = useMemo(() => {
    return players.filter(p => p != null).map(p => {
      if (p.id === currentUserId && myHoleCards.length === 2) {
        return { ...p, holeCards: myHoleCards };
      }
      return p;
    });
  }, [players, currentUserId, myHoleCards]);

  const currentUserInGame = useMemo(() => safePlayers.some(p => p?.id === currentUserId), [safePlayers, currentUserId]);

  // ============================================================================
  // ACTION BUTTONS - State and logic for Check/Fold/Raise/Call/All-In
  // ============================================================================
  const [raiseAmount, setRaiseAmount] = useState<number>(0);
  const [showRaiseSlider, setShowRaiseSlider] = useState(false);
  // Refs mirror state into the turn-timer interval closure so the on-zero
  // handler can read the latest values without us having to re-create the
  // timer on every state change.
  const showRaiseSliderRef = useRef(false);
  const timeBankUsedRef = useRef(false);
  useEffect(() => { showRaiseSliderRef.current = showRaiseSlider; }, [showRaiseSlider]);
  // Sync timeBankUsed into the ref. Has to be declared after the useState
  // below; the ref itself is initialized above for stable identity.
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Current player
  const currentPlayer = useMemo(() => safePlayers.find(p => p?.id === currentUserId), [safePlayers, currentUserId]);
  
  // Is it current user's turn?
  const isCurrentPlayerTurn = useMemo(() => {
    if (!currentPlayer || currentPlayerIndex < 0) return false;
    const turnPlayer = safePlayers[currentPlayerIndex];
    return turnPlayer?.id === currentUserId;
  }, [currentPlayer, currentPlayerIndex, safePlayers, currentUserId]);

  // Current turn player
  const currentTurnPlayer = useMemo(() => {
    if (currentPlayerIndex < 0 || currentPlayerIndex >= safePlayers.length) return null;
    return safePlayers[currentPlayerIndex];
  }, [safePlayers, currentPlayerIndex]);

  // Max bet on table
  const maxBet = useMemo(() => {
    return Math.max(0, ...safePlayers.filter(p => p).map(p => p.currentBet || 0));
  }, [safePlayers]);

  // Call amount
  const callAmount = useMemo(() => currentPlayer ? maxBet - (currentPlayer.currentBet || 0) : 0, [maxBet, currentPlayer]);

  // Min raise
  const minRaise = useMemo(() => {
    const lastRaise = gameState.lastRaiseAmount || gameState.bigBlind;
    return Math.max(gameState.bigBlind, lastRaise);
  }, [gameState.bigBlind, gameState.lastRaiseAmount]);

  // Can take action?
  const canTakeAction = useMemo(() => {
    if (!isCurrentPlayerTurn) return false;
    if (gameState.status !== 'playing') return false;
    if (!gameState.round || gameState.round === 'ended' || gameState.round === 'waiting' || gameState.round === 'showdown') return false;
    if (!currentPlayer) return false;
    if (currentPlayer.folded || currentPlayer.allIn || currentPlayer.eliminated) return false;
    if (currentPlayer.waitingForNextHand || currentPlayer.needsRebuy) return false;
    return true;
  }, [isCurrentPlayerTurn, gameState.status, gameState.round, currentPlayer]);

  // AoF mode
  const isAoFMode = specialRules?.allInOrFold === true;

  // Possible actions
  const possibleActions = useMemo(() => {
    if (!currentPlayer || !isCurrentPlayerTurn || currentPlayer.folded || currentPlayer.eliminated) return [];

    const actions: string[] = [];
    
    if (isAoFMode) {
      if (!currentPlayer.allIn) actions.push('fold');
      if (currentPlayer.chips > 0) actions.push('all-in');
      return actions;
    }
    
    if (!currentPlayer.allIn) actions.push('fold');
    
    if (callAmount === 0) {
      actions.push('check');
    } else if (currentPlayer.chips >= callAmount) {
      actions.push('call');
    }
    
    if (currentPlayer.chips > callAmount) {
      actions.push('raise');
    }
    
    if (currentPlayer.chips > 0) {
      actions.push('all-in');
    }

    return actions;
  }, [currentPlayer, isCurrentPlayerTurn, callAmount, isAoFMode]);

  // All-in showdown players
  const allInShowdownPlayers = useMemo(() => {
    if (gameState.round !== 'showdown' && gameState.round !== 'ended') return [];
    if (gameState.handResult?.allOthersFolded) return [];
    const nonFolded = safePlayers.filter(p => p && inShowdownHand(p));
    const hasAllIn = gameState.allInOccurred === true || gameState.allInShowdown === true;
    if (!hasAllIn) return [];
    return nonFolded;
  }, [gameState.round, safePlayers, gameState.handResult, gameState.allInShowdown, gameState.allInOccurred]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  const getSuitSymbol = (suit: string) => {
    switch (suit) {
      case 'hearts':   return '♥'; // ♥
      case 'diamonds': return '♦'; // ♦
      case 'clubs':    return '♣'; // ♣
      case 'spades':   return '♠'; // ♠
      default: return '';
    }
  };

  const getSuitColor = (suit: string) => suit === 'hearts' || suit === 'diamonds' ? '#dc2626' : '#1a1a1a';

  const getChipColor = (amount: number) => {
    if (amount >= 1000) return { fill: '#fbbf24', stroke: '#d97706' };
    if (amount >= 500) return { fill: '#a855f7', stroke: '#7c3aed' };
    if (amount >= 100) return { fill: '#374151', stroke: '#111827' };
    if (amount >= 25) return { fill: '#22c55e', stroke: '#15803d' };
    if (amount >= 5) return { fill: '#ef4444', stroke: '#b91c1c' };
    return { fill: '#fff', stroke: '#d1d5db' };
  };

  // Money must read EXACTLY at a cash table. The old formatter collapsed
  // anything >= 1000 to "1K" - a $1,450 stack displayed as "1K" and cents
  // vanished, so players had no idea what they actually had or won.
  // pkcash (real money): always exact with cents - $1,450.25 not "1K".
  // playcoins: whole coins with separators; abbreviate only at 100K+
  // where the trailing digits stop mattering.
  const formatChips = (n: number) => {
    const v = typeof n === 'number' && isFinite(n) ? n : 0;
    if (tableCurrency === 'pkcash') {
      return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (v >= 1000000) return `${(v / 1000000).toFixed(2)}M`;
    if (v >= 100000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString('en-US');
  };

  const hasValidCards = (cards: any[] | undefined): boolean => {
    if (!cards || cards.length < 2) return false;
    return cards[0]?.rank && cards[0].rank !== '' && cards[1]?.rank && cards[1].rank !== '';
  };

  const getPlayerDisplayName = (odid: string): string => {
    if (usernames[odid]) return usernames[odid];
    const player = safePlayers.find(p => p.id === odid);
    return player?.name || `Player ${odid.substring(0,6)}`;
  };

  // ============================================================================
  // RUNOUT VISIBLE CARDS (separate useMemo like Classic)
  // ============================================================================
  const runoutVisibleCards = useMemo(() => {
    if (runoutPhase === 'none' || runoutPhase === 'complete') {
      return null; // Use default behavior
    }
    return gameState.communityCards.slice(0, visibleBoardCount);
  }, [runoutPhase, gameState.communityCards, visibleBoardCount]);

  // ============================================================================
  // VISIBLE COMMUNITY CARDS (respects runout animation) - MATCHES CLASSIC
  // ============================================================================
  const visibleCommunityCards = useMemo(() => {
    // During all-in runout, let runout control visibility
    if (runoutVisibleCards !== null && runoutPhase !== 'none' && runoutPhase !== 'complete') {
      return runoutVisibleCards;
    }
    
    // Simple round-based visibility - EXACTLY like Classic
    const round = gameState?.round || 'waiting';
    const cards = gameState?.communityCards || [];
    
    switch (round) {
      case 'preflop':
      case 'waiting':
        return [];
      case 'flop':
        return cards.slice(0, 3);
      case 'turn':
        return cards.slice(0, 4);
      case 'river':
      case 'showdown':
      case 'ended':
      default:
        return cards.slice(0, 5);
    }
  }, [gameState?.communityCards, gameState?.round, runoutVisibleCards, runoutPhase]);

  // Get hand strength
  const getHandStrength = useCallback(() => {
    if (!currentPlayer || !hasValidCards(currentPlayer.holeCards)) return '';
    if (runoutPhase === 'reveal-cards') return '';
    const hand = gameEngine.evaluateHand(currentPlayer.holeCards!, visibleCommunityCards);
    return hand?.rank || '';
  }, [currentPlayer, visibleCommunityCards, runoutPhase]);

  // ============================================================================
  // SYNC VOICE ANNOUNCER
  // ============================================================================
  useEffect(() => { voiceAnnouncer.setEnabled(voiceEnabled); }, [voiceEnabled]);
  useEffect(() => { if (dealerImageUrl) voiceAnnouncer.setVoiceFromDealerImage(dealerImageUrl); }, [dealerImageUrl]);

  // Auto-clear action error
  useEffect(() => {
    if (actionError) {
      const timer = setTimeout(() => setActionError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionError]);

  // Initialize raise amount when turn STARTS (not on every re-render)
  useEffect(() => {
    const isMyTurn = !!(currentPlayer && isCurrentPlayerTurn);
    
    // Only initialize when it BECOMES my turn, not when it already was
    if (isMyTurn && !wasMyTurnRef.current) {
      setRaiseAmount(Math.min(minRaise, currentPlayer!.chips));
      setShowRaiseSlider(false); // Reset slider visibility on new turn
      triggerHaptic('medium');
    }
    
    // Update ref for next render
    wasMyTurnRef.current = isMyTurn;
  }, [currentPlayer, isCurrentPlayerTurn, minRaise]);

  // ============================================================================
  // MASTER CLEANUP ON UNMOUNT
  // ============================================================================
  useEffect(() => {
    return () => {
      if (turnTimerRef.current) clearInterval(turnTimerRef.current);
      if (runoutTimerRef.current) clearTimeout(runoutTimerRef.current);
    };
  }, []);

  // ============================================================================
  // CACHE POT
  // ============================================================================
  useEffect(() => {
    if (gameState.pot > 0 && runoutPhase === 'none') {
      setCachedPot(Math.round(gameState.pot * 100) / 100);
    }
  }, [gameState.pot, runoutPhase]);

  // Keep timeBankUsedRef in sync so the turn-timer interval (created with
  // stable deps below) can read the latest value.
  useEffect(() => { timeBankUsedRef.current = timeBankUsed; }, [timeBankUsed]);

  // ============================================================================
  // TURN TIMER - 30 second countdown with auto-fold
  // ============================================================================
  useEffect(() => {
    if (gameState.handNumber !== lastHandForTimerRef.current) {
      lastHandForTimerRef.current = gameState.handNumber;
      setTimeBankUsed(false);
      setTurnTimeLeft(TURN_DURATION);
    }

    if (gameState.status !== 'playing' || gameState.round === 'ended' || gameState.round === 'showdown') {
      if (turnTimerRef.current) { clearInterval(turnTimerRef.current); turnTimerRef.current = null; }
      return;
    }

    const turnPlayer = safePlayers[gameState?.currentPlayerIndex];
    if (!turnPlayer || !turnPlayer.id || turnPlayer.folded || turnPlayer.allIn) return;

    let initialTime = TURN_DURATION;
    if (gameState.turnStartedAt) {
      const elapsed = Math.floor((Date.now() - gameState.turnStartedAt) / 1000);
      initialTime = Math.max(0, TURN_DURATION - elapsed);
    }
    setTurnTimeLeft(initialTime);

    if (turnTimerRef.current) clearInterval(turnTimerRef.current);

    turnTimerRef.current = setInterval(() => {
      setTurnTimeLeft(prev => {
        if (prev <= 0) {
          // Timer already at zero - someone else (most likely a bot) is
          // stalled. Ping /kickStalledTurn so the server kicks the bot
          // processor or force-folds. Throttled per turnStartedAt so we
          // only fire ~once every 3s while the stall persists.
          if (!isCurrentPlayerTurn) {
            const turnKey = `${tableId}:${gameState.turnStartedAt || 0}`;
            const last = lastKickRef.current;
            const nowMs = Date.now();
            if (!last || last.key !== turnKey || (nowMs - last.at) > 3000) {
              lastKickRef.current = { key: turnKey, at: nowMs };
              gameApiService.kickStalledTurn(tableId).catch(() => {/* silent - best-effort */});
            }
          }
          return 0;
        }
        const newTime = prev - 1;
        if (newTime <= 0 && isCurrentPlayerTurn && currentPlayer && !currentPlayer.folded) {
          // Time-out = auto-fold, period. Per user direction: the +10s time
          // bank is opt-in only via the explicit button, never auto-spent.
          // The previous auto-spend (when the raise slider was open) was
          // surprising - players expected to lose the hand and instead lost
          // their bank silently.
          gameApiService.timeoutFold(tableId).catch(err => console.error('Auto-fold failed:', err));
          if (turnTimerRef.current) { clearInterval(turnTimerRef.current); turnTimerRef.current = null; }
          return 0;
        }
        return Math.max(0, newTime);
      });
    }, 1000);

    return () => { if (turnTimerRef.current) { clearInterval(turnTimerRef.current); turnTimerRef.current = null; } };
  }, [gameState.currentPlayerIndex, gameState.status, gameState.round, gameState.handNumber, gameState.turnStartedAt, isCurrentPlayerTurn, currentPlayer, tableId, safePlayers]);

  const handleUseTimeBank = useCallback(() => {
    if (timeBankUsed || !isCurrentPlayerTurn) return;
    setTimeBankUsed(true);
    setTurnTimeLeft(prev => Math.min(prev + 10, TURN_DURATION + 10));
    playActionSound('check', soundEnabled);
  }, [timeBankUsed, isCurrentPlayerTurn, soundEnabled]);

  // ============================================================================
  // SHOW CARDS HANDLER (Fold Wins)
  // ============================================================================
  const isCurrentUserWinner = useMemo(() => {
    return winners?.some(w => w.playerId === currentUserId) || false;
  }, [winners, currentUserId]);

  const canShowCards = isFoldWin && isCurrentUserWinner && !showCardsChosen && !showCardsExpired && !handResult?.winnerShowedCards;

  // Timer countdown for show cards option
  useEffect(() => {
    if (!canShowCards) return;
    
    const timer = setInterval(() => {
      setShowCardsTimer(prev => {
        if (prev <= 1) {
          setShowCardsExpired(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [canShowCards]);

  // Reset show cards state when hand changes
  useEffect(() => {
    if (gameState.handNumber) {
      setShowCardsChosen(false);
      setShowCardsExpired(false);
      setShowCardsTimer(5);
    }
  }, [gameState.handNumber]);

  const handleShowCards = useCallback(async () => {
    setShowCardsChosen(true);
    try {
      await gameApiService.showCards(tableId);
      playActionSound('check', soundEnabled);
    } catch (err) {
      console.error('Failed to show cards:', err);
    }
  }, [tableId, soundEnabled]);

  // ============================================================================
  // RUN IT TWICE MODAL DETECTION & HANDLER
  // ============================================================================
  useEffect(() => {
    const offer = gameState.runItTwiceOffer as any;
    // A LIVE offer = exists, not fully responded, not declined. The old
    // check keyed on offer.active, which the server never used to write -
    // the picker never opened on this component (the live table for
    // premium AND every phone). Keyed on the real fields now.
    if (!offer || offer.allResponded || offer.declined) {
      setShowRITModal(false);
      ritOfferRef.current = null;
      return;
    }

    // Check if current user is involved
    if (!offer.players?.includes(currentUserId)) return;

    // Already picked: keep the panel up in waiting mode (JSX handles it)
    const offerId = `${gameState.handNumber}-${(offer.players || []).join('-')}`;
    ritOfferRef.current = offerId;
    setShowRITModal(true);
  }, [gameState.runItTwiceOffer, currentUserId, gameState.handNumber]);

  // RIT decision countdown: 25 seconds, then auto-answer "run it once" so
  // one absent tap can never stall the table (the server also has a 60s
  // rescue, but a connected client should resolve it long before that).
  const [ritCountdown, setRitCountdown] = useState<number | null>(null);
  const ritAutoRef = useRef<string | null>(null);
  useEffect(() => {
    const offer = gameState.runItTwiceOffer as any;
    const myTurnToPick = showRITModal && offer && !offer.allResponded && !offer.declined
      && offer.players?.includes(currentUserId)
      && offer.responses?.[currentUserId] === undefined;
    if (!myTurnToPick) { setRitCountdown(null); return; }
    const offerId = `${gameState.handNumber}-${(offer.players || []).join('-')}`;
    setRitCountdown(25);
    const iv = setInterval(() => {
      setRitCountdown(prev => {
        if (prev === null) return null;
        if (prev <= 1) {
          if (ritAutoRef.current !== offerId) {
            ritAutoRef.current = offerId;
            handleRITResponseRef.current?.(1);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRITModal, gameState.runItTwiceOffer, currentUserId, gameState.handNumber]);

  const handleRITResponse = useCallback(async (runs: number) => {
    setRitResponding(true);
    try {
      await gameApiService.respondToRunItTwice(tableId, runs);
      playActionSound(runs >= 2 ? 'check' : 'fold', soundEnabled);
    } catch (err) {
      console.error('RIT response failed:', err);
      setActionError('Failed to respond to Run It Multiple Times');
    } finally {
      setRitResponding(false);
    }
  }, [tableId, soundEnabled]);
  // Ref so the countdown timer always calls the CURRENT handler without
  // restarting itself when the callback identity changes.
  const handleRITResponseRef = useRef(handleRITResponse);
  handleRITResponseRef.current = handleRITResponse;

  // ============================================================================
  // STRADDLE DETECTION & HANDLER
  // ============================================================================
  useEffect(() => {
    if (!specialRules?.straddleEnabled) {
      setCanStraddle(false);
      return;
    }

    if (gameState.round !== 'waiting' && gameState.round !== 'preflop') {
      setCanStraddle(false);
      setStraddlePosted(false);
      return;
    }

    if (!currentPlayer || currentPlayer.folded || currentPlayer.eliminated) {
      setCanStraddle(false);
      return;
    }

    if (gameState.round === 'waiting') {
      setStraddlePosted(false);
    }

    if (gameState.straddlePlayerId === currentUserId) {
      setStraddlePosted(true);
      setCanStraddle(false);
      return;
    }

    setCanStraddle(gameState.round === 'waiting');
  }, [specialRules?.straddleEnabled, gameState.round, currentPlayer, currentUserId, gameState.straddlePlayerId]);

  const handlePostStraddle = useCallback(async () => {
    if (postingStraddle) return;
    setPostingStraddle(true);
    try {
      const straddleMult = Math.max(2, Number(specialRules?.straddleMin) || 2);
      const straddleAmount = (gameState.bigBlind || 2) * straddleMult;
      await gameApiService.postStraddle(tableId, straddleAmount);
      setStraddlePosted(true);
      setCanStraddle(false);
      playActionSound('raise', soundEnabled);
    } catch (err) {
      console.error('Straddle failed:', err);
      setActionError('Failed to post straddle');
    } finally {
      setPostingStraddle(false);
    }
  }, [tableId, gameState.bigBlind, postingStraddle, soundEnabled]);

  // ============================================================================
  // BOMB POT ANNOUNCEMENT
  // ============================================================================
  useEffect(() => {
    if (!gameState.isBombPot) return;
    if (gameState.handNumber === lastBombPotHandRef.current) return;

    lastBombPotHandRef.current = gameState.handNumber;
    setShowBombPotAnnouncement(true);
    playActionSound('allIn', soundEnabled);
    voiceAnnouncer.announce('Bomb Pot!');

    const timer = setTimeout(() => setShowBombPotAnnouncement(false), 3000);
    return () => clearTimeout(timer);
  }, [gameState.isBombPot, gameState.handNumber, soundEnabled]);

  // ============================================================================
  // 7-2 BOUNTY CELEBRATION
  // ============================================================================
  useEffect(() => {
    const sevenTwoWin = gameState.sevenTwoWinner;
    if (!sevenTwoWin) return;
    if (gameState.handNumber === lastSevenTwoHandRef.current) return;

    lastSevenTwoHandRef.current = gameState.handNumber;
    setSevenTwoBountyWinner(getPlayerDisplayName(sevenTwoWin.playerId));
    setSevenTwoBountyAmount(sevenTwoWin.bounty);
    setShowSevenTwoBounty(true);
    playActionSound('win', soundEnabled);

    const timer = setTimeout(() => setShowSevenTwoBounty(false), 5000);
    return () => clearTimeout(timer);
  }, [gameState.sevenTwoWinner, gameState.handNumber, soundEnabled]);

  // ============================================================================
  // ROUND ANNOUNCEMENTS
  // ============================================================================
  useEffect(() => {
    if (prevRoundRef.current === gameState.round) return;
    
    const prevRound = prevRoundRef.current;
    prevRoundRef.current = gameState.round;

    let announcement = '';
    if (gameState.round === 'flop') {
      announcement = 'FLOP';
      voiceAnnouncer.announceFlop();
    } else if (gameState.round === 'turn') {
      announcement = 'TURN';
      voiceAnnouncer.announceTurn();
    } else if (gameState.round === 'river') {
      announcement = 'RIVER';
      voiceAnnouncer.announceRiver();
    } else if (gameState.round === 'showdown') {
      announcement = 'SHOWDOWN';
      voiceAnnouncer.announceShowdown();
    }

    if (announcement) {
      setRoundAnnouncementText(announcement);
      setShowRoundAnnouncement(true);
      const timer = setTimeout(() => setShowRoundAnnouncement(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [gameState.round]);

  // ============================================================================
  // FETCH MY HOLE CARDS (SECURE)
  // ============================================================================
  useEffect(() => {
    if (!gameState.handNumber || !currentUserId || !tableId) return;
    if (gameState.handNumber === lastFetchedHandRef.current) return;
    if (gameState.round === 'ended' || gameState.status === 'ended') return;
    
    lastFetchedHandRef.current = gameState.handNumber;
    
    gameApiService.getMyCards(tableId).then(result => {
      if (result.success && result.holeCards?.length === 2) {
        setMyHoleCards(result.holeCards);
      }
    }).catch(() => {});
  }, [gameState.handNumber, gameState.round, gameState.status, currentUserId, tableId]);

  useEffect(() => {
    if (!gameState || gameState.status === 'waiting' || gameState.round === 'ended') {
      const timer = setTimeout(() => { setMyHoleCards([]); lastFetchedHandRef.current = null; }, 5000);
      return () => clearTimeout(timer);
    }
  }, [gameState?.status, gameState?.round]);

  // Track last playing round
  useEffect(() => {
    if (gameState.round !== 'showdown' && gameState.round !== 'ended') {
      lastPlayingRoundRef.current = gameState.round;
    }
  }, [gameState.round]);

  // ============================================================================
  // EQUITY CALCULATOR
  // ============================================================================
  //  FIXED: Accept boardCount as parameter to avoid stale state

  const calculateEquities = useCallback(async (boardCount: number) => {
    console.log('📊 [EQUITY] calculateEquities called with boardCount:', boardCount);
    
    if (allInShowdownPlayers.length < 2) {
      console.log('📊 [EQUITY] Not enough players:', allInShowdownPlayers.length);
      return;
    }
    
    // Only calculate for players who have valid (revealed) hole cards
    //  Hidden cards have rank='' which would break equity calculation

    const playersWithCards = allInShowdownPlayers.filter(p => 
      p.holeCards?.length === 2 && 
      p.holeCards[0]?.rank && p.holeCards[0].rank !== '' &&
      p.holeCards[1]?.rank && p.holeCards[1].rank !== ''
    );
    if (playersWithCards.length < 2) {
      console.log('📊 [EQUITY] Not enough players with valid hole cards:', playersWithCards.length);
      return;
    }
    
    // Get board cards directly from gameState
    const boardToUse = gameState.communityCards.slice(0, boardCount);
    console.log('📊 [EQUITY] Board cards:', boardToUse.length, boardToUse.map(c => `${c.rank}${c.suit[0]}`));
    
    try {
      const { calculateEquity } = await import('../services/equityCalculator');
      
      const playersData = playersWithCards.map(p => ({
        id: p.id,
        holeCards: p.holeCards!,
      }));

      const results = calculateEquity(playersData, boardToUse, 1000);
      
      const equityMap: { [playerId: string]: number } = {};
      for (const r of results) {
        equityMap[r.playerId] = r.equity;
      }
      
      console.log(`📊 [EQUITY] Results for ${boardCount} cards:`, equityMap);
      setPlayerEquities(equityMap);
    } catch (err) {
      console.error('📊 [EQUITY] Failed to calculate:', err);
    }
  }, [allInShowdownPlayers, gameState.communityCards]);

  // ============================================================================
  // RUNOUT ANIMATION
  // ============================================================================
  useEffect(() => {
    // Only trigger once per hand when entering showdown with multiple players
    if (gameState.handNumber === lastRunoutHandRef.current) return;
    if (gameState.round !== 'showdown' && gameState.round !== 'ended') return;
    
    console.log('🎬 [RUNOUT] Checking for all-in:', {
      allInOccurred: gameState.allInOccurred,
      allInShowdown: gameState.allInShowdown,
      round: gameState.round,
      handNumber: gameState.handNumber,
      communityCards: gameState.communityCards?.length || 0
    });
    
    //  FIX #1: Use hand-level flag, not player.allIn which gets cleared

    const hasAllIn = gameState.allInOccurred === true || gameState.allInShowdown === true;
    const allInPlayers = hasAllIn ? safePlayers.filter(p => p && inShowdownHand(p)) : [];
    
    console.log('🎬 [RUNOUT] hasAllIn:', hasAllIn, 'allInPlayers:', allInPlayers.length, 'safePlayers:', safePlayers.map(p => ({ id: p?.id?.substring(0,8), folded: p?.folded })));
    
    //  FIX: If not enough players for runout, mark as complete immediately

    if (allInPlayers.length < 2) {
      console.log('🎬 [RUNOUT] Skipping - only', allInPlayers.length, 'players for runout - marking complete');
      lastRunoutHandRef.current = gameState.handNumber;
      setRunoutPhase('complete');
      runoutActiveRef.current = false;
      return;
    }

    //  CRITICAL: Determine how many community cards were ALREADY visible

    const previousRound = lastPlayingRoundRef.current;
    let startingBoardCount = 0;
    switch (previousRound) {
      case 'preflop': startingBoardCount = 0; break;
      case 'flop': startingBoardCount = 3; break;
      case 'turn': startingBoardCount = 4; break;
      case 'river': startingBoardCount = 5; break;
      default: startingBoardCount = 0;
    }
    
    console.log('🎬 [RUNOUT] Starting all-in suspense for', allInPlayers.length, 'players');
    console.log('🎬 [RUNOUT] Previous round:', previousRound, '| Starting board count:', startingBoardCount);
    lastRunoutHandRef.current = gameState.handNumber;
    
    runoutActiveRef.current = true;
    
    // Cache pot at runout start
    if (gameState.pot > 0) {
      setCachedPot(Math.round(gameState.pot * 100) / 100);
    }
    
    //  FIXED: Start with existing board cards, don't reset to 0!

    setRunoutPhase('reveal-cards');
    setRevealedPlayerIndex(-1);
    setRevealedCardIndex(-1);
    setVisibleBoardCount(startingBoardCount);
    setPlayerEquities({});

    if (runoutTimerRef.current) clearTimeout(runoutTimerRef.current);

    let playerIdx = 0;
    let cardIdx = 0;
    let boardCount = startingBoardCount;
    
    // Tighter timings - old values felt sluggish during all-in runouts,
    // especially for testers iterating tournaments. Still suspenseful but
    // doesn't make every hand feel like a TV broadcast.
    const CARD_REVEAL_DELAY = 300;
    const PLAYER_SWITCH_DELAY = 400;
    const BOARD_CARD_DISPLAY = 1200;
    const RIVER_SUSPENSE = 1000;
    
    const sequence = () => {
      // Phase 1: Reveal hole cards one by one
      if (playerIdx < allInPlayers.length) {
        if (cardIdx < 2) {
          setRevealedPlayerIndex(playerIdx);
          setRevealedCardIndex(cardIdx);
          console.log(`🎬 [RUNOUT] Revealing player ${playerIdx} card ${cardIdx}`);
          cardIdx++;
          runoutTimerRef.current = setTimeout(sequence, CARD_REVEAL_DELAY);
        } else {
          playerIdx++;
          cardIdx = 0;
          runoutTimerRef.current = setTimeout(sequence, PLAYER_SWITCH_DELAY);
        }
        return;
      }

      // Phase 2: If we started at 0, show flop first
      if (boardCount === 0) {
        console.log('🎬 [RUNOUT] Dealing flop (was preflop all-in)');
        setRunoutPhase('show-equity');
        boardCount = 3;
        setVisibleBoardCount(3);
        calculateEquities(3).catch(err => console.error('Equity calc failed:', err));
        runoutTimerRef.current = setTimeout(sequence, BOARD_CARD_DISPLAY);
        return;
      }
      
      // Phase 2b: If we started at 3 (flop), just calculate equity first
      if (boardCount === 3 && startingBoardCount === 3) {
        console.log('🎬 [RUNOUT] Calculating equity on existing flop');
        setRunoutPhase('show-equity');
        calculateEquities(3).catch(err => console.error('Equity calc failed:', err));
        // Small pause then continue to turn
        runoutTimerRef.current = setTimeout(() => {
          boardCount = 3.5; // Flag to move to turn next
          sequence();
        }, BOARD_CARD_DISPLAY);
        return;
      }

      // Phase 3: Deal turn (if needed)
      if (boardCount === 3 || boardCount === 3.5) {
        console.log('🎬 [RUNOUT] Dealing turn');
        setRunoutPhase('deal-turn');
        boardCount = 4;
        setVisibleBoardCount(4);
        runoutTimerRef.current = setTimeout(() => {
          calculateEquities(4).catch(err => console.error('Equity calc failed:', err));
          runoutTimerRef.current = setTimeout(sequence, BOARD_CARD_DISPLAY);
        }, 500);
        return;
      }
      
      // Phase 3b: If we started at 4 (turn), just calculate equity first
      if (boardCount === 4 && startingBoardCount === 4) {
        console.log('🎬 [RUNOUT] Calculating equity on existing turn');
        setRunoutPhase('show-equity');
        calculateEquities(4).catch(err => console.error('Equity calc failed:', err));
        // Small pause then continue to river
        runoutTimerRef.current = setTimeout(() => {
          boardCount = 4.5; // Flag to move to river next
          sequence();
        }, BOARD_CARD_DISPLAY);
        return;
      }

      // Phase 4: Deal river
      if (boardCount === 4 || boardCount === 4.5) {
        console.log('🎬 [RUNOUT] Dealing river');
        setRunoutPhase('deal-river');
        boardCount = 5;
        setVisibleBoardCount(5);
        runoutTimerRef.current = setTimeout(() => {
          runoutActiveRef.current = false;
          setRunoutPhase('complete');
          console.log('🎬 [RUNOUT] Sequence complete - announcing winner now');
        }, RIVER_SUSPENSE);
        return;
      }
      
      // Phase 5: If started at river (5), just complete immediately
      if (boardCount === 5 || startingBoardCount === 5) {
        console.log('🎬 [RUNOUT] River already dealt, completing');
        runoutActiveRef.current = false;
        setRunoutPhase('complete');
        return;
      }
    };

    runoutTimerRef.current = setTimeout(sequence, 500);

    return () => {
      if (runoutTimerRef.current) clearTimeout(runoutTimerRef.current);
    };
  //  CRITICAL FIX: Only handNumber + round as dependencies!

  // When server sends showdown result, player state changes and would cancel animation.
  }, [gameState.handNumber, gameState.round]);

  // ============================================================================
  // RESOLVE SHOWDOWN
  // ============================================================================
  useEffect(() => {
    if (runoutPhase !== 'complete') return;
    if (gameState.round !== 'showdown' && gameState.round !== 'ended') return;
    if (!gameState.allInShowdown && !gameState.allInOccurred) return;
    if (gameState.showdownComplete || (gameState.winners && gameState.winners.length > 0)) return;
    if (lastResolvedHandRef.current === gameState.handNumber) return;
    
    lastResolvedHandRef.current = gameState.handNumber;
    gameApiService.resolveAllInShowdown(tableId).catch(err => console.error('Resolve error:', err));
  }, [runoutPhase, gameState.allInShowdown, gameState.allInOccurred, gameState.showdownComplete, gameState.winners, gameState.handNumber, tableId, gameState.round]);

  // ============================================================================
  // WINNER DETECTION
  // ============================================================================
  useEffect(() => {
    if (gameState.round !== 'ended' || !gameState.winners || gameState.winners.length === 0) return;

    // RUN IT MULTIPLE TIMES: per-run winner rows aggregate into ONE readable
    // card ("You won 2 of 3 runs"). Bypasses the runout gate below - RIT
    // hands never go through the client runout animation, their reveal is
    // the staged board display, so wait for that instead.
    const ritRows = gameState.winners.filter((w: any) => (w as any).run !== undefined);
    if (ritRows.length > 0) {
      const ritCount = Math.max(...ritRows.map((w: any) => (w as any).run));
      if (ritRunsVisible < ritCount) return; // boards still revealing
      const winnerIdRIT = `${gameState.winners.map(w => w.playerId).join('-')}-${gameState.handNumber}`;
      if (winnerIdRIT === lastWinnerNotificationId) return;
      setLastWinnerNotificationId(winnerIdRIT);
      const cur = (n: number) => `${tableCurrency === 'pkcash' ? '$' : ''}${formatChips(Math.round(n * 100) / 100)}`;
      const mine = ritRows.filter(w => w.playerId === currentUserId);
      const myTotal = Math.round(mine.reduce((s, w) => s + (w.amount || 0), 0) * 100) / 100;
      // A run is WON when I was its only winner, CHOPPED when we tied
      const runsIWon = new Set<number>();
      const runsChopped = new Set<number>();
      mine.forEach((w: any) => {
        const others = ritRows.filter((o: any) => o.run === w.run && o.playerId !== currentUserId);
        (others.length > 0 ? runsChopped : runsIWon).add(w.run);
      });
      const perRun = Array.from({ length: ritCount }, (_, i) => i + 1).map(r => {
        const rows = ritRows.filter((w: any) => w.run === r);
        const names = rows.map((w: any) => (w.playerId === currentUserId ? 'You' : getPlayerDisplayName(w.playerId))).join(' & ');
        const hand = (rows[0] as any)?.hand;
        return `Run ${r}: ${names || '-'}${rows.length > 1 ? ' (chop)' : ''}${hand ? ` · ${hand}` : ''}`;
      });
      // Net against what I put in: a chop can still be a loss overall
      const myRowRIT = safePlayers.find((p: any) => p && p.id === currentUserId);
      const myInvestedRIT = Math.round(((myRowRIT as any)?.totalBetThisHand || 0) * 100) / 100;
      const myNetRIT = Math.round((myTotal - myInvestedRIT) * 100) / 100;
      if (myTotal > 0 && (myNetRIT > 0 || runsIWon.size > 0)) {
        if (winnerIdRIT !== lastCelebrationHandId && myNetRIT > 0) {
          setLastCelebrationHandId(winnerIdRIT);
          setCelebrationAmount(myTotal);
          setShowCelebration(true);
          playActionSound('win', soundEnabled);
        }
        const title = runsIWon.size === ritCount ? `You Won All ${ritCount} Runs`
          : runsChopped.size > 0 && runsIWon.size === 0 ? `Chopped ${runsChopped.size} of ${ritCount} Runs`
          : `You Won ${runsIWon.size}${runsChopped.size ? `, Chopped ${runsChopped.size}` : ''} of ${ritCount} Runs`;
        setNotificationData({
          type: myNetRIT >= 0 ? 'win' : 'lose',
          title,
          handName: (mine[0] as any)?.hand || null,
          message: myNetRIT >= 0 ? `${cur(myTotal)} to you (up ${cur(myNetRIT)})` : `${cur(myTotal)} back, down ${cur(Math.abs(myNetRIT))} overall`,
          detailLines: perRun,
        });
      } else {
        const totals = new Map<string, number>();
        ritRows.forEach((w: any) => totals.set(w.playerId, (totals.get(w.playerId) || 0) + (w.amount || 0)));
        const top = [...totals.entries()].sort((a, b) => b[1] - a[1])[0];
        setNotificationData({
          type: 'lose',
          title: `Ran It ${ritCount} Times`,
          handName: null,
          message: top ? `${getPlayerDisplayName(top[0])} took ${cur(top[1])}` : 'Hand complete',
          detailLines: perRun,
        });
      }
      setShowNotification(true);
      return;
    }

    const isFoldWinLocal = gameState.handResult?.allOthersFolded === true;
    if (!isFoldWinLocal) {
      const needsRunout = gameState.allInOccurred === true || gameState.allInShowdown === true;
      if (needsRunout && runoutPhase !== 'complete') return;
    }
    
    const winnerId = `${gameState.winners.map(w => w.playerId).join('-')}-${gameState.handNumber}`;
    if (winnerId === lastWinnerNotificationId) return;
    
    console.log('🏆 [PREMIUM] Showing winner notification!', winnerId);
    
    setLastWinnerNotificationId(winnerId);
    // Uncalled-bet returns ride in winners[] flagged `uncalled` - a player's
    // own chips coming back, never a win.
    const realWinners = gameState.winners.filter((w: any) => !w.uncalled);
    const currentUserWin = realWinners.find(w => w.playerId === currentUserId);
    const myUncalled = realWinners.find((w: any) => w.playerId === currentUserId && w.uncalled);
    const totalPot = realWinners.reduce((sum, w) => sum + (w.amount || 0), 0);
    
    // A true split = several winners of the SAME pot. Side pots also produce
    // several winner rows, so require equal amounts too - close enough for
    // the announcement, and the themed table previously had no split case at
    // all: a chopped pot read as a plain "You Won!", which on a phone looked
    // like the pot came back short for no reason.
    const isTrueSplitPot = realWinners.length > 1
      && new Set(realWinners.map(w => Math.round((w.amount || 0) * 100))).size === 1;

    if (currentUserWin) {
      // Judge by NET, not by having a winners[] row - a returned side pot
      // or uncalled bet appears there too, and announcing a win (or a
      // "split pot") to a player who lost the showdown while covered is
      // simply wrong. The celebration and the win sound follow the same rule.
      const myRow = safePlayers.find((p: any) => p && p.id === currentUserId);
      const myInvested = Math.round(((myRow as any)?.totalBetThisHand || 0) * 100) / 100;
      const myNet = Math.round((currentUserWin.amount - myInvested) * 100) / 100;
      const isFoldWinHere = gameState.handResult?.allOthersFolded === true || (currentUserWin as any).hand === 'Fold Win';
      if ((isFoldWinHere || myNet > 0) && winnerId !== lastCelebrationHandId) {
        setLastCelebrationHandId(winnerId);
        setCelebrationAmount(currentUserWin.amount);
        setShowCelebration(true);
        playActionSound('win', soundEnabled);
      }
      const otherWinners = realWinners.filter(w => w.playerId !== currentUserId);
      const topOther = [...otherWinners].sort((a, b) => (b.amount || 0) - (a.amount || 0))[0];

      if (myNet < 0 && topOther) {
        setNotificationData({
          type: 'lose',
          title: `${getPlayerDisplayName(topOther.playerId)} won the hand`,
          handName: (topOther as any).hand || null,
          message: `You lost $${Math.abs(myNet).toFixed(2)}`,
          detailLines: [`$${currentUserWin.amount.toFixed(2)} returned to you (side pot / uncalled chips)`],
        });
      } else if (isTrueSplitPot) {
        const otherNames = otherWinners.map(w => getPlayerDisplayName(w.playerId)).join(' & ');
        setNotificationData({
          type: 'win',
          title: 'Split Pot - You Won',
          handName: (currentUserWin as any).hand || null,
          message: `$${currentUserWin.amount.toFixed(2)} to you`,
          detailLines: [`Chopped with ${otherNames}`],
        });
      } else if (topOther && myNet >= 0) {
        setNotificationData({
          type: 'win',
          title: 'You Won the Side Pot',
          handName: (currentUserWin as any).hand || null,
          message: `$${currentUserWin.amount.toFixed(2)} to you (up $${myNet.toFixed(2)})`,
          detailLines: [`Main pot went to ${getPlayerDisplayName(topOther.playerId)}`],
        });
      } else {
        setNotificationData({
          type: 'win',
          title: 'You Won',
          handName: (currentUserWin as any).hand || null,
          message: `$${currentUserWin.amount.toFixed(2)} added to your stack`,
        });
      }
    } else {
      const wasInHand = safePlayers.some(p => p && p.id === currentUserId && !p.folded);
      if (wasInHand) playActionSound('lose', soundEnabled);
      const primaryWinner = [...realWinners].sort((a, b) => (b.amount || 0) - (a.amount || 0))[0];
      if (isTrueSplitPot) {
        const winnerNames = realWinners.map(w => getPlayerDisplayName(w.playerId)).join(' & ');
        const totalPot = realWinners.reduce((s, w) => s + (w.amount || 0), 0);
        setNotificationData({
          type: 'lose',
          title: 'Split Pot',
          handName: (realWinners.find(w => (w as any).hand) as any)?.hand || null,
          message: `${winnerNames} chopped the $${totalPot.toFixed(2)} pot`,
        });
      } else {
        setNotificationData({
          type: 'lose',
          title: `${getPlayerDisplayName(primaryWinner.playerId)} won the hand`,
          handName: (primaryWinner as any).hand || null,
          message: `$${primaryWinner.amount.toFixed(2)} pot`,
          ...(myUncalled ? { detailLines: [`$${Number(myUncalled.amount || 0).toFixed(2)} of your uncalled chips returned to you`] } : {}),
        });
      }
    }
    setShowNotification(true);
  }, [gameState.round, gameState.winners, gameState.handNumber, gameState.handResult, currentUserId, lastWinnerNotificationId, lastCelebrationHandId, runoutPhase, soundEnabled, safePlayers, gameState.allInOccurred, gameState.allInShowdown, ritRunsVisible, tableCurrency]);

  // ============================================================================
  // AUTO-START NEW HAND
  // ============================================================================
  useEffect(() => {
    if (gameState.round !== 'ended' || !gameState.winners || gameState.winners.length === 0) return;
    
    const isFoldWinLocal = gameState.handResult?.allOthersFolded === true;
    const isAllInShowdown = gameState.allInOccurred === true || gameState.allInShowdown === true;
    
    if (isFoldWinLocal) return; // gameProgressionService handles fold wins
    if (!isAllInShowdown) return;
    if (runoutPhase !== 'complete') return;
    
    setNextHandCountdown(8);
    const countdownInterval = setInterval(() => setNextHandCountdown(prev => Math.max(0, prev - 1)), 1000);
    
    const timer = setTimeout(async () => {
      clearInterval(countdownInterval);
      try {
        const gs = gameStateRef.current;
        const validPlayers = (gs.players || []).filter((p: any) => p && p.id && !p.eliminated);
        const playersWithChips = validPlayers.filter(p => p.chips > 0 && !p.waitingForNextHand);
        
        if (playersWithChips.length < 2) {
          await new Promise(r => setTimeout(r, 2000));
          const latest = gameStateRef.current;
          const latestWithChips = (latest.players || []).filter((p: any) => p && p.id && !p.eliminated && p.chips > 0 && !p.waitingForNextHand);
          if (latestWithChips.length < 2) { await tableService.resetTableToLobby(tableId); return; }
        }
        await gameApiService.startHand(tableId);
      } catch (err) { console.error('Failed to start new hand:', err); }
    }, 8000);
    
    return () => { clearTimeout(timer); clearInterval(countdownInterval); };
  }, [gameState.round, gameState.winners, gameState.handResult, tableId, runoutPhase, gameState.allInOccurred, gameState.allInShowdown]);

  // ============================================================================
  // VOICE ANNOUNCEMENTS
  // ============================================================================
  useEffect(() => {
    if (prevPlayerIndexRef.current !== gameState.currentPlayerIndex && gameState.status === 'playing' && gameState.round !== 'ended') {
      prevPlayerIndexRef.current = gameState.currentPlayerIndex;
      const turnPlayer = safePlayers[gameState.currentPlayerIndex];
      if (turnPlayer?.id === currentUserId && !turnPlayer.folded && !turnPlayer.allIn) {
        voiceAnnouncer.announceYourTurn();
      }
    }
  }, [gameState.currentPlayerIndex, gameState.status, gameState.round, safePlayers, currentUserId]);

  useEffect(() => {
    if (gameState.status !== 'playing' || gameState.round === 'ended') return;
    safePlayers.forEach(player => {
      if (!player?.id || player.id === currentUserId) return;
      const prevAction = prevActionsRef.current[player.id];
      const currentAction = player.lastAction;
      if (currentAction && currentAction !== prevAction) {
        const name = getPlayerDisplayName(player.id);
        if (currentAction === 'fold') voiceAnnouncer.announceFold(name);
        else if (currentAction === 'check') voiceAnnouncer.announceCheck(name);
        else if (currentAction === 'call') voiceAnnouncer.announceCall(name);
        else if (currentAction === 'raise') voiceAnnouncer.announceRaise(name);
        else if (currentAction === 'all-in') voiceAnnouncer.announceAllIn(name);
      }
      prevActionsRef.current[player.id] = currentAction || '';
    });
  }, [safePlayers, gameState.status, gameState.round, currentUserId]);

  useEffect(() => {
    if (prevRoundRef.current !== gameState.round) {
      prevRoundRef.current = gameState.round;
      if (gameState.round === 'flop') voiceAnnouncer.announceFlop();
      else if (gameState.round === 'turn') voiceAnnouncer.announceTurn();
      else if (gameState.round === 'river') voiceAnnouncer.announceRiver();
      else if (gameState.round === 'showdown') voiceAnnouncer.announceShowdown();
    }
  }, [gameState.round]);

  // Reset on new hand
  useEffect(() => {
    if (gameState.round === 'preflop') {
      prevWinnerRef.current = null;
      prevActionsRef.current = {};
      runoutActiveRef.current = false;
      setRunoutPhase('none');
      setVisibleBoardCount(0);
      setPlayerEquities({});
    }
  }, [gameState.round]);

  // ============================================================================
  // REBUY HANDLER
  // ============================================================================
  const handleRebuy = async (amount: number) => {
    if (!tableId || !currentUserId) return;
    setIsRebuyProcessing(true);
    try {
      const result = await gameApiService.secureRebuy(tableId, amount);
      if (result.success) setShowRebuyModal(false);
      else alert(`Rebuy failed: ${result.error}`);
    } catch (err) { alert('Rebuy failed.'); }
    finally { setIsRebuyProcessing(false); }
  };

  // Subscribe to game items animations
  useEffect(() => {
    if (!tableId) return;
    const unsubscribe = gameItemsService.subscribeToAnimations(tableId, (animations: ActiveAnimation[]) => setActiveAnimations(animations));
    return () => unsubscribe();
  }, [tableId]);

  //  GAME ITEMS: Load PSS Coins balance

  useEffect(() => {
    const loadBalance = async () => {
      if (!currentUserId) return;
      const wallet = await walletService.getWallet(currentUserId);
      if (wallet) {
        setPkCashBalance(wallet.pkCash);
      }
    };
    loadBalance();
  }, [currentUserId]);

  //  GAME ITEMS: Handle balance change after item purchase

  const handleBalanceChange = useCallback((newBalance: number) => {
    setPkCashBalance(newBalance);
  }, []);

  // Handle player action
  const handlePlayerAction = async (action: string) => {
    if (!canTakeAction || !tableId || !currentPlayer) return;

    setIsProcessing(true);
    setActionError(null);
    setShowRaiseSlider(false);

    try {
      playActionSound(action, soundEnabled);
      
      const playerName = usernames[currentUserId] || currentPlayer.name || 'Player';
      switch (action) {
        case 'fold': voiceAnnouncer.announceFold(playerName); break;
        case 'check': voiceAnnouncer.announceCheck(playerName); break;
        case 'call': voiceAnnouncer.announceCall(playerName); break;
        case 'raise': voiceAnnouncer.announceRaise(playerName, raiseAmount); break;
        case 'all-in': voiceAnnouncer.announceAllIn(playerName); break;
      }

      const result = await gameApiService.playerAction(
        tableId,
        action as 'fold' | 'check' | 'call' | 'raise' | 'all-in',
        action === 'raise' ? raiseAmount : undefined,
        gameState.handId,
        gameState.stateVersion
      );

      if (!result.success && !result.error?.includes('state has changed')) {
        throw new Error(result.error || 'Action failed');
      }

      setRaiseAmount(0);
    } catch (err: any) {
      console.error('Action failed:', err);
      setActionError(err.message || 'Action failed');
    } finally {
      setIsProcessing(false);
    }
  };

  // ⚡ PRE-ACTIONS: arm fold/check/call-any while waiting for the turn and
  // fire it the moment the turn arrives (same behavior as the classic
  // table - ported here because premium tables never had it). Pre-check is
  // invalidated by a bet: control returns to the player instead of
  // auto-checking into a raise.
  useEffect(() => { setPreAction(null); }, [gameState.handNumber]);

  useEffect(() => {
    if (!preAction || !canTakeAction || isProcessing) return;
    const facingBet = callAmount > 0;
    let action: 'fold' | 'check' | 'call' | null = null;
    if (preAction === 'checkFold') action = facingBet ? 'fold' : 'check';
    else if (preAction === 'check') action = facingBet ? null : 'check';
    else if (preAction === 'callAny') action = facingBet ? 'call' : 'check';
    setPreAction(null);
    if (action) handlePlayerAction(action);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preAction, canTakeAction, isProcessing, callAmount]);

  // ============================================================================
  // SEAT ROTATION - Current user ALWAYS at bottom (position 0)
  // Other players placed on SIDES (not top, not next to current user)
  // ============================================================================
  const playerToVisualPosition = useMemo(() => {
    const posMap = new Map<string, number>();
    if (safePlayers.length === 0) return posMap;
    
    const currentUserIndex = safePlayers.findIndex(p => p?.id === currentUserId);
    
    //  FIXED: Dynamic seat positioning that avoids dealer overlap

    // Dealer is at top-center (position = maxSeats/2), so we need to handle this carefully
    // The top-centre seat (position maxSeats/2) is where the dealer stands
    // on themed tables. Nobody is ever seated there: it is her spot. Tables
    // that fill past the seats around her put the last arrivals on the
    // half-positions either side of her (2.5 and 3.5 on a six-seat table),
    // which getSeatPosition understands.
    const hasDealer = !!dealerImageUrl;
    const dealerPos = Math.floor(maxSeats / 2);
    const keepDealerSeatFree = (list: number[], numOthers: number): number[] => {
      if (!hasDealer) return list;
      let out = list.filter(p => p !== dealerPos);
      if (out.length >= numOthers) return out.slice(0, numOthers).sort((a, b) => a - b);
      const spare = Array.from({ length: maxSeats - 1 }, (_, i) => i + 1)
        .filter(p => p !== dealerPos && !out.includes(p))
        .sort((a, b) => Math.abs(a - dealerPos) - Math.abs(b - dealerPos));
      while (out.length < numOthers && spare.length > 0) out.push(spare.shift() as number);
      if (out.length < numOthers) out.push(dealerPos - 0.5);
      if (out.length < numOthers) out.push(dealerPos + 0.5);
      return out.sort((a, b) => a - b);
    };

    const getOtherPlayerPositions = (numOthers: number): number[] => {
      if (numOthers === 0) return [];

      // Portrait: sparse tables read wrong when opponents pile into the
      // top corners - prefer the side rails first (CoinPoker seating),
      // then the top, so a heads-up bot sits across, two bots flank you.
      if (isPortrait) {
        const pref = maxSeats === 8 ? [2, 6, 4, 3, 5, 1, 7]
          : maxSeats === 6 ? [2, 4, 3, 1, 5]
            : maxSeats === 9 ? [2, 7, 4, 5, 3, 6, 1, 8]
              : maxSeats === 10 ? [2, 8, 5, 3, 7, 4, 6, 1, 9]
                : null;
        if (pref) {
          const usable = hasDealer ? pref.filter(p => p !== dealerPos) : pref;
          if (numOthers <= usable.length) return usable.slice(0, numOthers).sort((a, b) => a - b);
          return keepDealerSeatFree([...usable, dealerPos], numOthers);
        }
      }

      // Six-seat themed table (the Sophia lounges): her seat is 3, so the
      // guests take the two top corners first, then the bottom corners.
      if (maxSeats === 6 && hasDealer) {
        if (numOthers === 1) return [2];
        if (numOthers === 2) return [2, 4];
        if (numOthers === 3) return [2, 4, 5];
        if (numOthers === 4) return [1, 2, 4, 5];
        return [1, 2, 2.5, 3.5, 4].slice(0, numOthers);
      }

      // For small tables (2-8 seats), use optimized static positions
      if (maxSeats <= 8) {
        if (numOthers === 1) return keepDealerSeatFree([3], numOthers);
        if (numOthers === 2) return keepDealerSeatFree([3, 5], numOthers);
        if (numOthers === 3) return keepDealerSeatFree([2, 3, 5], numOthers);
        if (numOthers === 4) return keepDealerSeatFree([2, 3, 5, 6], numOthers);
        if (numOthers === 5) return keepDealerSeatFree([1, 2, 3, 5, 6], numOthers);
        if (numOthers === 6) return keepDealerSeatFree([1, 2, 3, 5, 6, 7], numOthers);
        // For 7 others on 8-seat table, include position 4 but getSeatPosition will adjust Y
        if (numOthers === 7) return keepDealerSeatFree([1, 2, 3, 4, 5, 6, 7], numOthers);
        return keepDealerSeatFree([1, 2, 3, 5, 6, 7], numOthers);
      }
      
      // For 9-seat tables
      if (maxSeats === 9) {
        // Dealer is at position 4 (9/2 = 4.5 → 4)
        const nine = numOthers === 1 ? [4]
          : numOthers === 2 ? [3, 5]
          : numOthers === 3 ? [2, 4, 6]
          : numOthers === 4 ? [2, 3, 5, 6]
          : numOthers === 5 ? [1, 2, 4, 6, 7]
          : numOthers === 6 ? [1, 2, 3, 5, 6, 7]
          : numOthers === 7 ? [1, 2, 3, 4, 5, 6, 7]
          : numOthers === 8 ? [1, 2, 3, 4, 5, 6, 7, 8]
          : [1, 2, 3, 5, 6, 7, 8];
        return keepDealerSeatFree(nine, numOthers);
      }

      // For 10-seat tables
      if (maxSeats === 10) {
        // Dealer is at position 5 (10/2 = 5)
        const ten = numOthers === 1 ? [5]
          : numOthers === 2 ? [3, 7]
          : numOthers === 3 ? [3, 5, 7]
          : numOthers === 4 ? [2, 4, 6, 8]
          : numOthers === 5 ? [2, 3, 5, 7, 8]
          : numOthers === 6 ? [1, 2, 4, 6, 8, 9]
          : numOthers === 7 ? [1, 2, 3, 5, 7, 8, 9]
          : numOthers === 8 ? [1, 2, 3, 4, 6, 7, 8, 9]
          : numOthers === 9 ? [1, 2, 3, 4, 5, 6, 7, 8, 9]
          : [1, 2, 3, 4, 6, 7, 8, 9];
        return keepDealerSeatFree(ten, numOthers);
      }

      // Fallback for any other configuration
      return keepDealerSeatFree([1, 2, 3, 5, 6, 7], numOthers);
    };
    
    if (currentUserIndex === -1) {
      const positions = [0, ...getOtherPlayerPositions(safePlayers.length - 1)];
      safePlayers.forEach((p, idx) => { if (p?.id && idx < positions.length) posMap.set(p.id, positions[idx]); });
      return posMap;
    }
    
    const cp = safePlayers[currentUserIndex];
    if (cp?.id) posMap.set(cp.id, 0);
    
    const otherPlayers = safePlayers.filter((p, idx) => idx !== currentUserIndex && p?.id);
    const otherPositions = getOtherPlayerPositions(otherPlayers.length);
    otherPlayers.forEach((player, idx) => { if (player?.id && idx < otherPositions.length) posMap.set(player.id, otherPositions[idx]); });
    
    return posMap;
  }, [safePlayers, currentUserId, maxSeats, isPortrait]);

  const visualPositionToPlayer = useMemo(() => {
    const map = new Map<number, Player | undefined>();
    safePlayers.forEach(p => {
      if (!p?.id) return;
      const pos = playerToVisualPosition.get(p.id);
      if (pos !== undefined) map.set(pos, p);
    });
    return map;
  }, [safePlayers, playerToVisualPosition]);

  const getSeatPosition = useCallback((visualPos: number) => {
    if (isPortrait) {
      const anchors = PORTRAIT_SEATS[maxSeats] || PORTRAIT_SEATS[8];
      if (!Number.isInteger(visualPos)) {
        // Half position beside the dealer: midway between its neighbours
        const lo = anchors[Math.floor(visualPos) % anchors.length];
        const hi = anchors[Math.ceil(visualPos) % anchors.length];
        return { x: (lo[0] + hi[0]) / 2, y: (lo[1] + hi[1]) / 2 };
      }
      const a = anchors[visualPos] || anchors[visualPos % anchors.length];
      return { x: a[0], y: a[1] };
    }
    const angleStep = 360 / maxSeats;
    const angle = 90 - (visualPos * angleStep);
    const rad = (angle * Math.PI) / 180;

    let x = CENTER_X + SEATS_RADIUS_X * Math.cos(rad);
    let y = CENTER_Y + SEATS_RADIUS_Y * Math.sin(rad);

    //  FIXED: Push top-center seat up to avoid dealer image overlap

    // Dealer position is at the top center (position = maxSeats / 2)
    const dealerPos = Math.floor(maxSeats / 2);

    // Check if this seat is at or very near the top center (within 1 position)
    // This catches position 4 for 8-seat, position 4-5 for 9-seat, position 5 for 10-seat
    const isNearDealer = Math.abs(visualPos - dealerPos) <= 0;

    if (isNearDealer && visualPos !== 0) {
      // Push the seat up by 60 pixels to clear the dealer image
      y -= 60;
    }

    return { x, y };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxSeats, isPortrait]);

  const getBetPosition = useCallback((visualPos: number) => {
    if (isPortrait) {
      // Bet pill sits on the felt, 130 units in from the seat toward center
      const anchors = PORTRAIT_SEATS[maxSeats] || PORTRAIT_SEATS[8];
      let a = anchors[visualPos] || anchors[visualPos % anchors.length];
      if (!Number.isInteger(visualPos)) {
        const lo = anchors[Math.floor(visualPos) % anchors.length];
        const hi = anchors[Math.ceil(visualPos) % anchors.length];
        a = [(lo[0] + hi[0]) / 2, (lo[1] + hi[1]) / 2];
      }
      const dx = CENTER_X - a[0];
      const dy = CENTER_Y - a[1];
      const len = Math.hypot(dx, dy) || 1;
      return { x: a[0] + (dx / len) * 130, y: a[1] + (dy / len) * 130 };
    }
    const angleStep = 360 / maxSeats;
    const angle = 90 - (visualPos * angleStep);
    const rad = (angle * Math.PI) / 180;
    return { x: CENTER_X + (TABLE_RX - 60) * Math.cos(rad), y: CENTER_Y + (TABLE_RY - 40) * Math.sin(rad) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxSeats, isPortrait]);

  const lastPositionsRef = useRef<string>('');

  useEffect(() => {
    const positions: { [id: string]: { x: number; y: number } } = {};
    safePlayers.forEach(player => {
      if (!player?.id) return;
      const visualPos = playerToVisualPosition.get(player.id);
      if (visualPos !== undefined) positions[player.id] = getSeatPosition(visualPos);
    });
    const positionsKey = JSON.stringify(positions);
    if (positionsKey !== lastPositionsRef.current) {
      lastPositionsRef.current = positionsKey;
      setPlayerPositions(positions);
      if (onPlayerPositionsUpdate) onPlayerPositionsUpdate(positions);
    }
  }, [safePlayers, playerToVisualPosition, onPlayerPositionsUpdate, getSeatPosition]);

  // Card rendering - polished casino look. Face cards have a clean white
  // body with crisp red/black corner indices and a centered suit pip; card
  // backs use a layered red gradient with a subtle diamond pattern to read
  // as professional from across the felt instead of a flat color block.
  const renderCard = (card: Card | undefined, x: number, y: number, w: number, h: number, key: string) => {
    if (!card?.rank) {
      // Subtle drop shadow + double border + repeating-pattern back.
      return (
        <g key={key} transform={`translate(${x}, ${y})`}>
          <rect width={w} height={h} rx="5" fill="rgba(0,0,0,0.45)" transform="translate(1.5, 2)" />
          <rect width={w} height={h} rx="5" fill="url(#cardBackGrad)" stroke="#7a0c1c" strokeWidth="1" />
          <rect x="2.5" y="2.5" width={w - 5} height={h - 5} rx="3.5" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.75" />
          {/* Diamond pattern */}
          <g opacity="0.18" transform={`translate(${w / 2}, ${h / 2})`}>
            {[-1, 0, 1].map(row =>
              [-1, 0, 1].map(col => (
                <rect key={`d-${row}-${col}`} x={col * 7 - 2.5} y={row * 9 - 2.5} width="5" height="5"
                  fill="#fff" transform={`rotate(45 ${col * 7} ${row * 9})`} />
              ))
            )}
          </g>
        </g>
      );
    }
    const color = getSuitColor(card.suit);
    const suit = getSuitSymbol(card.suit);
    // Scale by card width so face cards read at every size (community vs hole).
    const indexFont = Math.max(11, w * 0.32);
    const suitTopFont = Math.max(9, w * 0.26);
    const pipFont = Math.max(18, w * 0.55);
    return (
      <g key={key} transform={`translate(${x}, ${y})`}>
        {/* Drop shadow */}
        <rect width={w} height={h} rx="5" fill="rgba(0,0,0,0.45)" transform="translate(1.5, 2)" />
        {/* Card body with a slight top sheen */}
        <rect width={w} height={h} rx="5" fill="#fafafa" stroke="#9aa0a6" strokeWidth="0.75" />
        <rect width={w} height={h * 0.45} rx="5" fill="url(#cardSheen)" opacity="0.45" />
        {/* Top-left index */}
        <text x={w * 0.13} y={indexFont + 1} fill={color} fontSize={indexFont} fontWeight="800"
          fontFamily="'Inter','Helvetica Neue',Arial,sans-serif" textAnchor="start">{card.rank}</text>
        <text x={w * 0.13} y={indexFont + suitTopFont + 1} fill={color} fontSize={suitTopFont}
          textAnchor="start">{suit}</text>
        {/* Centered pip */}
        <text x={w / 2} y={h / 2 + pipFont * 0.35} textAnchor="middle" fill={color} fontSize={pipFont}>
          {suit}
        </text>
        {/* Bottom-right index, rotated 180 like a real deck. Rotating the
            top-left index around the card center lands it exactly in the
            opposite corner, inverted. */}
        <g transform={`rotate(180 ${w / 2} ${h / 2})`}>
          <text x={w * 0.13} y={indexFont + 1} fill={color} fontSize={indexFont} fontWeight="800"
            fontFamily="'Inter','Helvetica Neue',Arial,sans-serif" textAnchor="start">{card.rank}</text>
          <text x={w * 0.13} y={indexFont + suitTopFont + 1} fill={color} fontSize={suitTopFont}
            textAnchor="start">{suit}</text>
        </g>
      </g>
    );
  };

  // ============================================================================
  // RENDER SEATS
  // ============================================================================
  const renderSeats = useMemo(() => {
    const seats = [];
    // Sitting-out players take empty visual slots, in order
    const awayQueue = (awaySeated || []).filter(a => a && a.id && !playerToVisualPosition.has(a.id));

    // Every integer slot except the dealer's (no "+" marker under her),
    // plus any half-slots a full table placed beside her.
    const dealerSlot = dealerImageUrl ? Math.floor(maxSeats / 2) : -1;
    const slots: number[] = Array.from({ length: maxSeats }, (_, i) => i).filter(v => v !== dealerSlot);
    playerToVisualPosition.forEach((v) => { if (!Number.isInteger(v) && !slots.includes(v)) slots.push(v); });
    slots.sort((a, b) => a - b);

    for (const vPos of slots) {
      const pos = getSeatPosition(vPos);
      const betPos = getBetPosition(vPos);
      const player = visualPositionToPlayer.get(vPos);
      const isMyTurn = player && safePlayers[currentPlayerIndex]?.id === player.id && !player.folded;
      const isMe = player?.id === currentUserId;
      const isWinner = player && winners.some(w => w.playerId === player.id);
      const isWaiting = player?.waitingForNextHand || player?.needsRebuy;

      // Early exit for empty seats - check before accessing player properties
      if (!player) {
        const away = awayQueue.length > 0 ? awayQueue.shift()! : null;
        if (away) {
          const awayName = away.name || 'Player';
          seats.push(
            <g key={`away-${vPos}`}
              transform={`translate(${pos.x}, ${pos.y})${isPortrait ? ' scale(1.15)' : ''}`}
              opacity={0.8}>
              <circle r="40" fill="#101820" stroke="#2b3648" strokeWidth="3" />
              {userPhotos[away.id] ? (
                <>
                  <defs><clipPath id={`awayclip-${away.id}`}><circle r="37" /></clipPath></defs>
                  <image href={userPhotos[away.id]} x="-37" y="-37" width="74" height="74"
                    clipPath={`url(#awayclip-${away.id})`} opacity="0.5" />
                </>
              ) : (
                <text y="8" textAnchor="middle" fill="#64748b" fontSize="22" fontWeight="bold">
                  {awayName.charAt(0).toUpperCase()}
                </text>
              )}
              <g transform="translate(0, 55)">
                <rect x="-65" y="-10" width="130" height="42" rx="8"
                  fill="rgba(0,0,0,0.75)" stroke="#334155" strokeWidth="1.5" />
                <text y="8" textAnchor="middle" fill="#94a3b8" fontSize="13" fontWeight="700">
                  {awayName.length > 16 ? awayName.slice(0, 15) + '…' : awayName}
                </text>
                <text y="26" textAnchor="middle" fill="#eab308" fontSize="10"
                  fontWeight="800" letterSpacing="1">SITTING OUT</text>
              </g>
            </g>
          );
          continue;
        }
        const hovered = hoveredSeat === vPos;
        const clickable = onSeatClick && !currentUserInGame;
        
        seats.push(
          <g key={`empty-${vPos}`} transform={`translate(${pos.x}, ${pos.y})${isPortrait ? ' scale(1.15)' : ''}`}
            style={{ cursor: clickable ? 'pointer' : 'default' }}
            onClick={() => clickable && onSeatClick(vPos)}
            onMouseEnter={() => clickable && setHoveredSeat(vPos)}
            onMouseLeave={() => setHoveredSeat(null)}
          >
            <circle r="35" fill={hovered ? 'rgba(34,197,94,0.3)' : 'rgba(30,41,59,0.7)'} 
              stroke={hovered ? '#22c55e' : 'rgba(255,255,255,0.25)'} strokeWidth={hovered ? 3 : 2} strokeDasharray={hovered ? '' : '5 5'} />
            <text y={hovered ? 0 : 6} textAnchor="middle" dominantBaseline="middle" 
              fill={hovered ? '#22c55e' : 'rgba(255,255,255,0.5)'} fontSize={hovered ? 11 : 26} fontWeight="bold">
              {hovered ? 'SIT HERE' : '+'}
            </text>
          </g>
        );
        continue;
      }

      const hasCards = player.holeCards?.length === 2 && player.holeCards[0]?.rank;

      const shouldShowAtShowdown = isShowdown && !isFoldWin && !player.folded && hasCards;
      // Own player always sees own cards - even after folding (dimmed) so they can remember what they laid down.
      const shouldShowMyCards = isMe && hasCards;
      const showCards = !isWaiting && (shouldShowMyCards || shouldShowAtShowdown);
      const ownFoldedDim = isMe && player.folded;

      // Equity badge
      const playerEquity = playerEquities[player.id];
      const showEquity = playerEquity !== undefined
        && ((isRunoutInProgress && runoutPhase !== 'reveal-cards') || ritRevealActive);

      const name = usernames[player.id] || player.name || 'Player';
      const isBotSeat = typeof player.id === 'string' && player.id.startsWith('house_bot_');
      // Photo source order: house bots get PSS character art; humans get
      // uploaded photo > player.photoURL > DiceBear fallback (deterministic
      // per UID - every player gets a cartoon identity even before they
      // pick one).
      const photo = isBotSeat
        ? pssBustForBot(player.id)
        : (userPhotos[player.id] || player.photoURL || getDefaultAvatarUrl(player.id));

      seats.push(
        <g key={`player-${vPos}`}>
          {/* Bet chips on felt */}
          {player.currentBet && player.currentBet > 0 && !isShowdown && (
            <g transform={`translate(${betPos.x}, ${betPos.y})${isPortrait ? ' scale(1.15)' : ''}`}>
              {player.currentBet >= 50 && <ellipse cy="4" rx="16" ry="8" fill={getChipColor(player.currentBet).fill} stroke={getChipColor(player.currentBet).stroke} strokeWidth="1.5" />}
              <ellipse rx="16" ry="8" fill={getChipColor(player.currentBet).fill} stroke={getChipColor(player.currentBet).stroke} strokeWidth="2" />
              <rect x="-34" y="12" width="68" height="18" rx="9" fill="rgba(0,0,0,0.85)" stroke="#fbbf24" strokeWidth="1" />
              <text y="24" textAnchor="middle" fill="#fbbf24" fontSize="11" fontWeight="bold">{formatChips(player.currentBet)}</text>
            </g>
          )}

          {/* Player panel. Tapping it opens the profile sheet - this prop was
              being passed in and silently dropped, so on themed tables no
              player was inspectable at all. */}
          <g transform={`translate(${pos.x}, ${pos.y})${isPortrait ? ' scale(1.15)' : ''}`}
            className={`seat-group ${isMyTurn ? 'seat-active' : ''} ${isWinner ? 'seat-winner' : ''}`}
            onClick={onPlayerClick ? () => onPlayerClick(player.id, name) : undefined}
            style={onPlayerClick ? { cursor: 'pointer' } : undefined}>
            {/* Winner glow */}
            {isWinner && <ellipse rx="60" ry="55" fill="none" stroke="#FFD700" strokeWidth="4" opacity="0.7" className="winner-glow" />}

            {/* Cards above player. Portrait hero: the cards deal INTO the
                seat, fanned beside the avatar in table coordinates - they
                scale with the felt like every other card, instead of the
                old DOM overlay that floated over the middle of the table. */}
            {showCards && player.holeCards && (
              isPortrait && isMe ? (
                <g style={{ opacity: ownFoldedDim ? 0.45 : 1 }}>
                  {/* Identity key remounts the card when its face becomes
                      known, replaying the deal-in animation - the animation
                      covers render time so a face can never visibly change
                      after it appears. Class lives on an INNER group so the
                      CSS transform can't stomp the positioning transform. */}
                  <g transform="translate(-64, -138) rotate(-5)">
                    <g key={`hc0-${player.holeCards[0]?.rank || 'x'}${player.holeCards[0]?.suit || ''}`} className="prem-card-in">
                      {renderCard(player.holeCards[0], 0, 0, 62, 87, `${player.id}-c0`)}
                    </g>
                  </g>
                  <g transform="translate(-4, -142) rotate(6)">
                    <g key={`hc1-${player.holeCards[1]?.rank || 'x'}${player.holeCards[1]?.suit || ''}`} className="prem-card-in">
                      {renderCard(player.holeCards[1], 0, 0, 62, 87, `${player.id}-c1`)}
                    </g>
                  </g>
                </g>
              ) : (
                <g transform={isPortrait ? 'translate(-48, -120)' : 'translate(-25, -95)'}
                  style={{ opacity: ownFoldedDim ? 0.45 : 1 }}>
                  <g key={`oc0-${player.holeCards[0]?.rank || 'x'}${player.holeCards[0]?.suit || ''}`} className="prem-card-in">
                    {renderCard(player.holeCards[0], 0, 0, isPortrait ? 46 : 35, isPortrait ? 64 : 50, `${player.id}-c0`)}
                  </g>
                  <g key={`oc1-${player.holeCards[1]?.rank || 'x'}${player.holeCards[1]?.suit || ''}`} className="prem-card-in">
                    {renderCard(player.holeCards[1], isPortrait ? 50 : 25, 0, isPortrait ? 46 : 35, isPortrait ? 64 : 50, `${player.id}-c1`)}
                  </g>
                </g>
              )
            )}

            {/* Card backs for others. Portrait: tucked behind the avatar's
                shoulder (CoinPoker-style) instead of floating above it. */}
            {!showCards && !player.folded && !isShowdown && !isWaiting && round !== 'waiting' && (
              isPortrait ? (
                <g>
                  <g transform="translate(12, -68) rotate(12)">{renderCard(undefined, 0, 0, 38, 54, `${player.id}-b0`)}</g>
                  <g transform="translate(30, -72) rotate(24)">{renderCard(undefined, 0, 0, 38, 54, `${player.id}-b1`)}</g>
                </g>
              ) : (
                <g transform="translate(-25, -95)">
                  {renderCard(undefined, 0, 0, 35, 50, `${player.id}-b0`)}
                  {renderCard(undefined, 25, 0, 35, 50, `${player.id}-b1`)}
                </g>
              )
            )}

            {/* Turn timer ring with countdown */}
            {isMyTurn && !player.folded && !player.allIn && round !== 'ended' && round !== 'showdown' && (
              <>
                <circle r="48" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
                <circle r="48" fill="none" stroke={turnTimeLeft <= 10 ? '#ef4444' : '#22c55e'} strokeWidth="4"
                  strokeDasharray={`${(turnTimeLeft / TURN_DURATION) * 301.6} 301.6`}
                  strokeLinecap="round"
                  transform="rotate(-90)" style={{ transition: 'stroke-dasharray 1s linear' }} />
                {/* Countdown number */}
                <circle r="14" cx="0" cy="-48" fill="rgba(0,0,0,0.85)" stroke={turnTimeLeft <= 10 ? '#ef4444' : '#22c55e'} strokeWidth="1.5" />
                <text x="0" y="-44" textAnchor="middle" fill={turnTimeLeft <= 10 ? '#ef4444' : '#fff'} fontSize="13" fontWeight="bold">{turnTimeLeft}</text>
              </>
            )}

            {/* Avatar */}
            <circle r="40" fill={isWinner ? '#FFD700' : isMe ? '#3b82f6' : '#4a5568'} stroke={isMyTurn ? '#22c55e' : isWinner ? '#FFD700' : '#2d3748'} strokeWidth="3" />
            
            {photo ? (
              <>
                <defs><clipPath id={`clip-${player.id}`}><circle r="37" /></clipPath></defs>
                <image href={photo} x="-37" y="-37" width="74" height="74" clipPath={`url(#clip-${player.id})`} style={{ opacity: player.folded ? 0.4 : 1 }} />
                <circle r="39" fill="none" stroke={isMyTurn ? '#22c55e' : '#2d3748'} strokeWidth="2" />
              </>
            ) : (
              <text y="10" textAnchor="middle" fill={isWinner ? '#000' : '#fff'} fontSize="24" fontWeight="bold">
                {name.charAt(0).toUpperCase()}
              </text>
            )}

            {/* Folded */}
            {player.folded && (
              <>
                <circle r="40" fill="rgba(0,0,0,0.65)" />
                <text y="8" textAnchor="middle" fill="#888" fontSize="14" fontWeight="bold">FOLD</text>
              </>
            )}

            {/* Name/chips panel - wider so the full username is legible
                (CoinPoker-style readable plate, not a cramped chip). */}
            <g transform="translate(0, 55)">
              <rect x="-65" y="-10" width="130" height="42" rx="8"
                fill="rgba(0,0,0,0.9)"
                stroke={isMe ? '#3b82f6' : isMyTurn ? '#22c55e' : '#444'} strokeWidth="1.5" />
              <text y="8" textAnchor="middle" fill={isMe ? '#93c5fd' : '#fff'} fontSize="13" fontWeight="700">
                {name.length > 16 ? name.slice(0, 15) + '…' : name}
              </text>
              <text y="26" textAnchor="middle" fill="#fbbf24" fontSize="14" fontWeight="bold">
                {tableCurrency === 'pkcash' ? '$' : ''}{formatChips(player.chips)}
              </text>
              {/* 🤖 BOT disclosure. House players MUST be marked at the real
                  table - the tour and the history drawer already label them,
                  but the felt is where disclosure legally matters. Pinned to
                  the plate's top-right corner so the avatar circle (which
                  overlaps the plate's top-center) can never cover it. */}
              {typeof player.id === 'string' && player.id.startsWith('house_bot_') && (
                <g transform="translate(48, -10)">
                  <rect x="-17" y="-7" width="34" height="14" rx="7"
                    fill="#0d3b2e" stroke="#22c55e" strokeWidth="1" />
                  <text y="4" textAnchor="middle" fill="#4ade80" fontSize="9"
                    fontWeight="800" letterSpacing="1">BOT</text>
                </g>
              )}
            </g>

            {/* Dealer button */}
            {(gameState.dealerIndex === player.seat || gameState.dealerButton === player.seat) && (
              <g transform="translate(-45, -25)">
                <circle r="15" fill="#fff" stroke="#333" strokeWidth="2" />
                <text y="5" textAnchor="middle" fill="#000" fontSize="13" fontWeight="bold">D</text>
              </g>
            )}

            {/* Last-action tag (CoinPoker style) - small colored pill on the
                corner of the seat panel showing what this player just did.
                Hidden for the seat whose turn it is (the timer ring is the
                signal there), for folded seats (FOLD overlay covers it), and
                between hands. */}
            {!isMyTurn && !player.folded && player.lastAction && round !== 'ended' && round !== 'waiting' && (() => {
              const tag = String(player.lastAction).toUpperCase();
              const tagColors: Record<string, { bg: string; fg: string }> = {
                'RAISE':  { bg: '#f97316', fg: '#fff' },
                'CALL':   { bg: '#22c55e', fg: '#06281a' },
                'CHECK':  { bg: '#475569', fg: '#e2e8f0' },
                'ALL-IN': { bg: '#dc2626', fg: '#fff' },
              };
              const c = tagColors[tag];
              if (!c) return null;
              const w = Math.max(44, tag.length * 8 + 14);
              return (
                <g transform="translate(-40, 32)">
                  <rect x={-w / 2} y="-10" width={w} height="20" rx="10" fill={c.bg} stroke="rgba(0,0,0,0.4)" strokeWidth="1" />
                  <text y="4" textAnchor="middle" fill={c.fg} fontSize="10" fontWeight="bold" letterSpacing="0.5">{tag}</text>
                </g>
              );
            })()}

            {/* All-in badge */}
            {player.allIn && !player.folded && (
              <g transform="translate(40, -30)">
                <rect x="-25" y="-12" width="50" height="24" rx="12" fill="#f97316" stroke="#fff" strokeWidth="1.5" />
                <text y="5" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">ALL-IN</text>
              </g>
            )}

            {/* Equity badge */}
            {showEquity && (
              <g transform="translate(0, -110)">
                <rect x="-25" y="-12" width="50" height="24" rx="12" fill={playerEquity >= 50 ? '#22c55e' : '#ef4444'} />
                <text y="5" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">{playerEquity}%</text>
              </g>
            )}
          </g>
        </g>
      );
    }

    return seats;
  }, [maxSeats, safePlayers, currentPlayerIndex, currentUserId, isShowdown, isFoldWin, hoveredSeat, onSeatClick, onPlayerClick, currentUserInGame, round, winners, getSeatPosition, getBetPosition, visualPositionToPlayer, usernames, userPhotos, tableCurrency, gameState.dealerIndex, gameState.dealerButton, turnTimeLeft, playerEquities, isRunoutInProgress, runoutPhase, ritRevealActive, isPortrait, awaySeated]);

  // ============================================================================
  // RENDER POT
  // ============================================================================
  const renderPot = () => {
    const displayPot = isRunoutInProgress && cachedPot > 0 ? cachedPot : pot;
    if (displayPot <= 0) return null;
    if (isPortrait) {
      // Portrait: pot sits above the board, CoinPoker-style chip + pill
      return (
        <g transform={`translate(${CENTER_X}, 268)`}>
          <ellipse cx="-46" cy="-6" rx="15" ry="7" fill="#c93b47" stroke="#7e1f28" strokeWidth="2" />
          <ellipse cx="-46" cy="-12" rx="15" ry="7" fill="#e5484d" stroke="#8d2830" strokeWidth="2" />
          <rect x="-24" y="-20" width="140" height="32" rx="16" fill="rgba(5,10,16,0.85)" />
          <text x="-8" y="2" fontSize="17" fontWeight="700" fill="#7fd6f2">Pot</text>
          <text x="26" y="2" fontSize="18" fontWeight="800" fill="#ffffff">
            {tableCurrency === 'pkcash' ? '$' : ''}{formatChips(displayPot)}
          </text>
        </g>
      );
    }
    return (
      <g transform={`translate(${CENTER_X}, ${CENTER_Y + 55})`}>
        <ellipse cx="-15" cy="-22" rx="12" ry="6" fill="#22c55e" stroke="#166534" strokeWidth="1.5" />
        <ellipse cy="-24" rx="12" ry="6" fill="#3b82f6" stroke="#1d4ed8" strokeWidth="1.5" />
        <ellipse cx="15" cy="-22" rx="12" ry="6" fill="#ef4444" stroke="#991b1b" strokeWidth="1.5" />
        <rect x="-60" y="-10" width="120" height="30" rx="15" fill="rgba(0,0,0,0.85)" stroke="#fbbf24" strokeWidth="2" />
        <text y="12" textAnchor="middle" fill="#fbbf24" fontSize="18" fontWeight="bold">
          {tableCurrency === 'pkcash' ? '$' : ''}{formatChips(displayPot)}

        </text>
      </g>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <div className="premium-table-wrapper premium-style">
      {/* Dealer (desktop DOM overlay; portrait draws her inside the SVG
          behind the rail so she sits AT the table instead of over it).
          An empty dealerImageUrl means this table has no dealer at all. */}
      {!isPortrait && !!dealerImageUrl && (
      <div className="premium-dealer-container">
        {!dealerLoaded && !dealerError && <div className="premium-dealer-loading"><div className="loading-spinner" /></div>}
        {dealerError ? (
          <div className="premium-dealer-fallback"><span></span><span>DEALER</span></div>

        ) : (
          <img src={dealerImageUrl} alt="Dealer" className={`premium-dealer-image ${dealerLoaded ? 'loaded' : ''}`}
            onLoad={() => setDealerLoaded(true)} onError={() => setDealerError(true)} />
        )}
        {onTipDealer && <button className="premium-tip-button" onClick={() => {
          // Play Sofia tip thank you sound
          const tipSounds = ['/audio/sofia/sofia_dealertip1.mp3', '/audio/sofia/sofia_dealtip2.mp3'];
          const randomTip = tipSounds[Math.floor(Math.random() * tipSounds.length)];
          const audio = new Audio(randomTip);
          audio.volume = 0.8;
          audio.play().catch(() => {});
          onTipDealer();
        }}> TIP DEALER</button>}

      </div>
      )}

      {/* Global Turn Timer Banner - matches regular table */}
      {gameState.status === 'playing' && gameState.round !== 'ended' && gameState.round !== 'showdown' &&
       currentTurnPlayer && !currentTurnPlayer.folded && !currentTurnPlayer.allIn && (
        <div style={{
          position: 'absolute',
          top: isPortrait ? '92px' : '10px',
          bottom: 'auto',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '8px 20px',
          borderRadius: '25px',
          background: turnTimeLeft <= 10
            ? 'linear-gradient(135deg, rgba(255,0,0,0.9), rgba(200,0,0,0.9))'
            : turnTimeLeft <= 20
            ? 'linear-gradient(135deg, rgba(255,170,0,0.9), rgba(200,130,0,0.9))'
            : 'linear-gradient(135deg, rgba(0,100,0,0.9), rgba(0,80,0,0.9))',
          boxShadow: turnTimeLeft <= 10
            ? '0 0 20px rgba(255,0,0,0.8), 0 4px 15px rgba(0,0,0,0.5)'
            : '0 4px 15px rgba(0,0,0,0.5)',
          animation: turnTimeLeft <= 10 ? 'pulse 0.5s ease-in-out infinite' : 'none',
        }}>
          <svg width="40" height="40" viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
            <circle cx="20" cy="20" r="16" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round"
              strokeDasharray={`${(turnTimeLeft / TURN_DURATION) * 100.5} 100.5`}
              transform="rotate(-90 20 20)" style={{ transition: 'stroke-dasharray 1s linear' }} />
            <text x="20" y="24" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">{turnTimeLeft}</text>
          </svg>
          <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
            {isCurrentPlayerTurn ? 'YOUR TURN' : `${getPlayerDisplayName(currentTurnPlayer?.id || '')}'s turn`}
          </span>
        </div>
      )}

      {/* SVG Table */}
      <svg viewBox={VIEWBOX} className="premium-table-svg" preserveAspectRatio="xMidYMid meet">
        <defs>
          {/* Felt - deeper teal-green with a centered highlight; reads as
              premium casino cloth rather than the old flat lime. */}
          <radialGradient id="premiumFelt" cx="50%" cy="38%" r="78%">
            <stop offset="0%" stopColor="#2a8c5f" />
            <stop offset="55%" stopColor="#176849" />
            <stop offset="100%" stopColor="#0c3d2b" />
          </radialGradient>
          {/* Felt grain noise overlay */}
          <pattern id="feltGrain" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill="transparent" />
            <circle cx="1" cy="1" r="0.4" fill="rgba(255,255,255,0.04)" />
            <circle cx="4" cy="3" r="0.35" fill="rgba(0,0,0,0.05)" />
          </pattern>
          {/* Rail - polished mahogany */}
          <linearGradient id="premiumRail" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6b3a26" />
            <stop offset="35%" stopColor="#4a261a" />
            <stop offset="70%" stopColor="#2e160e" />
            <stop offset="100%" stopColor="#1c0d08" />
          </linearGradient>
          {/* Subtle rail highlight ring */}
          <linearGradient id="railHighlight" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,200,120,0.35)" />
            <stop offset="40%" stopColor="rgba(255,200,120,0.10)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </linearGradient>
          {/* Card back - dual-tone red with depth */}
          <linearGradient id="cardBackGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#d63146" />
            <stop offset="50%" stopColor="#b41c30" />
            <stop offset="100%" stopColor="#7c0d1f" />
          </linearGradient>
          {/* Card face top sheen */}
          <linearGradient id="cardSheen" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>

        {isPortrait ? (
          <>
            {/* Portrait racetrack. The dealer (premium tables only) draws
                BEFORE the rail and felt so their edges crop her cleanly -
                she reads as seated at the head of the table. */}
            {portraitHasDealer && !dealerError && (
              <>
                <radialGradient id="portraitDealerGlow" cx="50%" cy="60%" r="55%">
                  <stop offset="0%" stopColor="rgba(0,217,217,0.22)" />
                  <stop offset="100%" stopColor="rgba(0,217,217,0)" />
                </radialGradient>
                <ellipse cx={CENTER_X} cy={30} rx={230} ry={150} fill="url(#portraitDealerGlow)" />
                <image href={dealerImageUrl} x={CENTER_X - 142} y={-186} width={284} height={426}
                  preserveAspectRatio="xMidYMid meet"
                  onLoad={() => setDealerLoaded(true)} onError={() => setDealerError(true)} />
              </>
            )}
            <rect x={26} y={30} width={668} height={980} rx={330} fill="url(#premiumRail)" stroke="#0d0d15" strokeWidth="4" />
            <rect x={26} y={30} width={668} height={980} rx={330} fill="none" stroke="#c9a44a" strokeOpacity="0.55" strokeWidth="2" />
            <rect x={54} y={58} width={612} height={924} rx={304} fill="none" stroke="#000" strokeOpacity="0.55" strokeWidth="8" />
            <rect x={60} y={64} width={600} height={912} rx={298} fill="url(#premiumFelt)" />
            <rect x={82} y={86} width={556} height={868} rx={276} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="2" />
            {/* Tip pill on the felt under the dealer */}
            {portraitHasDealer && !dealerError && onTipDealer && (
              <g transform={`translate(${CENTER_X}, 132)`} style={{ cursor: 'pointer' }}
                onClick={() => {
                  const tipSounds = ['/audio/sofia/sofia_dealertip1.mp3', '/audio/sofia/sofia_dealtip2.mp3'];
                  const audio = new Audio(tipSounds[Math.floor(Math.random() * tipSounds.length)]);
                  audio.volume = 0.8;
                  audio.play().catch(() => {});
                  onTipDealer();
                }}>
                <rect x={-64} y={-18} width={128} height={36} rx={18} fill="rgba(5,10,16,0.72)"
                  stroke="#c9a44a" strokeOpacity="0.7" strokeWidth="1.5" />
                <text y={6} textAnchor="middle" fontSize="15" fontWeight="800" fill="#f5c04a" letterSpacing="1.5">TIP DEALER</text>
              </g>
            )}
            {/* Branding */}
            <text x={CENTER_X} y={CENTER_Y + 280} textAnchor="middle" fill="rgba(255,255,255,0.08)"
              fontSize="64" fontWeight="900" fontFamily="Arial Black" letterSpacing="6">PSS</text>
          </>
        ) : (
          <>
            {/* Rail */}
            <ellipse cx={CENTER_X} cy={CENTER_Y} rx={TABLE_RX + RAIL_WIDTH} ry={TABLE_RY + RAIL_WIDTH} fill="url(#premiumRail)" stroke="#0d0d15" strokeWidth="4" />

            {/* Dealer divot - LARGER to fit dealer */}
            <path d={`M ${CENTER_X-100} ${CENTER_Y-TABLE_RY-RAIL_WIDTH+5} Q ${CENTER_X-100} ${CENTER_Y-TABLE_RY-RAIL_WIDTH-55} ${CENTER_X} ${CENTER_Y-TABLE_RY-RAIL_WIDTH-55} Q ${CENTER_X+100} ${CENTER_Y-TABLE_RY-RAIL_WIDTH-55} ${CENTER_X+100} ${CENTER_Y-TABLE_RY-RAIL_WIDTH+5}`}
              fill="url(#premiumRail)" stroke="#0d0d15" strokeWidth="3" />

            {/* Felt extension into divot - dealer "workspace" */}
            <ellipse cx={CENTER_X} cy={CENTER_Y-TABLE_RY-10} rx="80" ry="25" fill="url(#premiumFelt)" />

            {/* Inner rail edge */}
            <ellipse cx={CENTER_X} cy={CENTER_Y} rx={TABLE_RX+4} ry={TABLE_RY+4} fill="none" stroke="#0d0d15" strokeWidth="5" />

            {/* Felt */}
            <ellipse cx={CENTER_X} cy={CENTER_Y} rx={TABLE_RX} ry={TABLE_RY} fill="url(#premiumFelt)" />
            <ellipse cx={CENTER_X} cy={CENTER_Y} rx={TABLE_RX-15} ry={TABLE_RY-15} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2" />

            {/* Branding */}
            <text x={CENTER_X-200} y={CENTER_Y+10} textAnchor="middle" fill="rgba(255,255,255,0.1)" fontSize="40" fontWeight="bold" fontFamily="Arial Black">PSS</text>
            <text x={CENTER_X+200} y={CENTER_Y+10} textAnchor="middle" fill="rgba(255,255,255,0.1)" fontSize="40" fontWeight="bold" fontFamily="Arial Black">PSS</text>
          </>
        )}

        {/* TOURNAMENT FELT INFO - dim lines below the pot chip, inside
            table coordinates so cards and seats can never collide. */}
        {centerInfo && centerInfo.length > 0 && (
          <g pointerEvents="none">
            {centerInfo.slice(0, 4).map((line, i, arr) => (
              <text
                key={`felt-info-${i}`}
                x={CENTER_X}
                // Anchored UPWARD from a fixed bottom line so extra rows
                // (HAND FOR HAND, pay jumps) grow toward the board instead
                // of down under the hero's hole cards.
                y={isPortrait
                  ? 648 - (arr.length - 1 - i) * 28
                  : CENTER_Y + 121 - (arr.length - 1 - i) * 13}
                textAnchor="middle"
                fill="rgba(226,232,240,0.45)"
                fontSize={isPortrait ? 19 : 11}
                fontWeight="600"
              >
                {line}
              </text>
            ))}
          </g>
        )}

        {renderPot()}
        {renderSeats}

        {/* COMMUNITY CARDS - Using Classic inline approach. Portrait gets
            near-double-size cards so the board reads at arm's length.
            Rendered AFTER the seats so revealed showdown cards on the side
            seats can never cover the board (river suit stays readable).
            Hidden while Run It Multiple Times boards are on the felt. */}
        {!ritRuns && <g transform={isPortrait
          ? `translate(${CENTER_X - 250}, 330)`
          : `translate(${CENTER_X - 147}, ${CENTER_Y - 55})`}>
          {[0, 1, 2, 3, 4].map((idx) => {
            const card = visibleCommunityCards?.[idx];
            const isCardVisible = idx < (visibleCommunityCards?.length || 0);
            const cardX = idx * (isPortrait ? 102 : 65); // card width + gap
            const cardW = isPortrait ? 92 : 55;
            const cardH = isPortrait ? 128 : 78;

            return (
              <g
                // Key includes the card identity so a NEW street remounts the
                // group and replays the deal-in animation. The animation class
                // lives on an INNER group: a CSS transform on THIS group would
                // override the positioning translate attribute and stack every
                // card at the board origin.
                key={`comm-card-${idx}-${card && card.rank ? `${card.rank}${card.suit}` : 'empty'}`}
                transform={`translate(${cardX}, 0)`}
              >
                <g className={isCardVisible && card && card.rank ? 'prem-card-in' : undefined}>
                {isCardVisible && card && card.rank && card.suit ? (
                  // Same professional face as the hole cards: corner indexes
                  // (top-left + inverted bottom-right) with a center pip.
                  renderCard(card, 0, 0, cardW, cardH, `commface-${idx}`)
                ) : (
                  <rect
                    x="0"
                    y="0"
                    width={cardW}
                    height={cardH}
                    rx="5"
                    fill="rgba(0, 0, 0, 0.25)"
                    stroke="rgba(255, 255, 255, 0.15)"
                    strokeWidth="1.5"
                    strokeDasharray="5 3"
                  />
                )}
                </g>
              </g>
            );
          })}
        </g>}

        {/* RUN IT MULTIPLE TIMES - all boards stacked ON the felt, revealed
            one run at a time. Winner and amount labeled per board so the
            whole table can follow who took which run. */}
        {ritRuns && (() => {
          const count = ritRuns.length;
          const cw = isPortrait ? (count === 2 ? 74 : 60) : (count === 2 ? 46 : 40);
          const ch = isPortrait ? (count === 2 ? 104 : 84) : (count === 2 ? 65 : 56);
          const step = cw + 8;
          const rowGap = isPortrait ? ch + 36 : ch + 24;
          const x0 = CENTER_X - (step * 5 - 8) / 2;
          const y0 = isPortrait
            ? (count === 2 ? 296 : 262)
            : CENTER_Y - (count === 2 ? 96 : 118);
          return (
            <g>
              {ritRuns.map((run: any, ri: number) => {
                const fullyShown = ri < ritRunsVisible;
                const isCurrent = ri === ritRunsVisible && ritPartialCards > 0;
                if (!fullyShown && !isCurrent) return null;
                const board = fullyShown
                  ? (run.communityCards || [])
                  : (run.communityCards || []).slice(0, ritPartialCards);
                const rowWinners = Array.isArray(run.winners) && run.winners.length > 0
                  ? run.winners
                  : (run.winner?.playerId ? [run.winner] : []);
                // Winner label only once the run has fully dealt - no spoilers
                const label = fullyShown && rowWinners.length > 0
                  ? `${rowWinners.map((w: any) => getPlayerDisplayName(w.playerId)).join(' & ')} +${tableCurrency === 'pkcash' ? '$' : ''}${formatChips(rowWinners.reduce((s: number, w: any) => s + (w.amount || 0), 0))}`
                  : '';
                const rowY = y0 + ri * rowGap;
                return (
                  <g key={`rit-run-${ri}`} className="prem-card-in">
                    <text x={x0 - 8} y={rowY + ch / 2 + 4} textAnchor="end"
                      fill="rgba(226,232,240,0.7)" fontSize={isPortrait ? 17 : 12} fontWeight="800">
                      {ri + 1}
                    </text>
                    {board.map((card: any, ci: number) => (
                      <g key={`rit-${ri}-${ci}-${card?.rank || 'x'}${card?.suit || ''}`}>
                        {renderCard(card, x0 + ci * step, rowY, cw, ch, `rit-${ri}-${ci}`)}
                      </g>
                    ))}
                    {label && (
                      <text x={CENTER_X} y={rowY + ch + (isPortrait ? 24 : 16)} textAnchor="middle"
                        fill="#ffd700" fontSize={isPortrait ? 18 : 12} fontWeight="800">
                        {label}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })()}

        {/* Portrait bot speech bubble - anchored over the speaking bot's
            head so there's never any doubt about who is talking. Desktop
            keeps the corner toast below. */}
        {isPortrait && botChatBubble && botChatBubbleSenderId && playerPositions[botChatBubbleSenderId] && (() => {
          const sp = playerPositions[botChatBubbleSenderId];
          const bx = Math.max(20, Math.min(SVG_WIDTH - 250, sp.x - 115));
          const by = Math.max(portraitHasDealer ? -185 : 5, sp.y - 205);
          return (
            <g style={{ pointerEvents: 'none' }}>
              <foreignObject x={bx} y={by} width={230} height={130}>
                <div style={{
                  display: 'inline-block',
                  maxWidth: '230px',
                  background: 'rgba(10,16,25,0.96)',
                  border: '1px solid rgba(0,217,217,0.5)',
                  borderRadius: '12px',
                  padding: '8px 11px',
                  fontFamily: "Inter,'Helvetica Neue',Arial,sans-serif",
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  animation: 'pssBubbleLife 4.5s ease forwards',
                }}>
                  <div style={{
                    fontSize: '11px', fontWeight: 800, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: '#00d9d9',
                  }}>{botChatBubbleName}</div>
                  <div style={{
                    fontSize: '13px', color: '#dbe4ee', lineHeight: 1.35,
                    marginTop: '2px', fontWeight: 500,
                  }}>{botChatBubble}</div>
                </div>
              </foreignObject>
            </g>
          );
        })()}

        {/* Portrait hand strength - sits under the hero plate (the DOM
            overlay that used to carry it is desktop-only now) */}
        {isPortrait && currentPlayer && !currentPlayer.folded && getHandStrength() && (() => {
          const label = getHandStrength() as string;
          const w = Math.max(96, label.length * 9 + 28);
          return (
            <g pointerEvents="none" transform={`translate(${CENTER_X}, 1132)`}>
              <rect x={-w / 2} y="-15" width={w} height="28" rx="14"
                fill="rgba(16,185,129,0.92)" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
              <text y="5" textAnchor="middle" fill="#fff" fontSize="15" fontWeight="800">{label}</text>
            </g>
          );
        })()}
      </svg>

      {/* YOUR HOLE CARDS - Corner Display */}
      {currentPlayer && hasValidCards(currentPlayer.holeCards) && !currentPlayer.folded && !currentPlayer.waitingForNextHand && !currentPlayer.needsRebuy && (
        <div className="premium-your-hand">
          {currentPlayer.holeCards!.slice(0, 2).map((card, idx) => {
            const isRed = card.suit === 'hearts' || card.suit === 'diamonds';
            return (
              <div key={idx} className={`premium-your-card ${isRed ? 'red' : 'black'}`}>
                <div className="card-corner top-left"><span className="rank">{card.rank}</span><span className="suit">{getSuitSymbol(card.suit)}</span></div>
                <div className="card-center-suit">{getSuitSymbol(card.suit)}</div>
                <div className="card-corner bottom-right"><span className="rank">{card.rank}</span><span className="suit">{getSuitSymbol(card.suit)}</span></div>
              </div>
            );
          })}
          {getHandStrength() && <div className="premium-hand-strength">{getHandStrength()}</div>}
        </div>
      )}

      {/* Game Items Toggle Button */}
      {currentUserInGame && (
        <button className="premium-items-toggle-btn" onClick={() => setShowItemsMenu(!showItemsMenu)} title="Game Items">
          <Gift size={20} />
          {pkCashBalance > 0 && (
            <span className="balance-badge">${pkCashBalance.toFixed(2)}</span>
          )}
        </button>
      )}

      {/* Game Items Menu */}
      {showItemsMenu && currentUserInGame && (
        <GameItemsMenu
          tableId={tableId}
          currentUserId={currentUserId}
          currentUserName={usernames[currentUserId] || 'Player'}
          players={safePlayers.filter(p => p && p.id !== currentUserId).map(p => ({
            id: p.id,
            name: usernames[p.id] || p.name || 'Player'
          }))}
          pkCashBalance={pkCashBalance}
          onClose={() => setShowItemsMenu(false)}
          onBalanceChange={handleBalanceChange}
          soundEnabled={false}
        />
      )}

      {/* Animation Layer */}
      {activeAnimations.length > 0 && activeAnimations.map((animation) => (
        <PlayerActionAnimation key={animation.id} animation={animation} senderPosition={playerPositions[animation.senderId]} targetPosition={playerPositions[animation.targetId]} />
      ))}

      {/* ⚡ PRE-ACTION BAR - shown while waiting for the turn: arm an action
          and it fires automatically when the turn arrives (same as classic) */}
      {!canTakeAction && currentPlayer && !currentPlayer.folded && !currentPlayer.allIn
        && !currentPlayer.eliminated && !currentPlayer.waitingForNextHand && !currentPlayer.needsRebuy
        && gameState.status === 'playing'
        && !!round && round !== 'ended'
        && round !== 'waiting' && round !== 'showdown' && (
        <div style={{
          position: 'fixed', bottom: '18px', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: '8px', zIndex: 1100,
        }}>
          {([
            { key: 'checkFold', label: 'CHECK / FOLD' },
            { key: 'check', label: 'CHECK' },
            { key: 'callAny', label: 'CALL ANY' },
          ] as const).map(opt => (
            <button
              key={opt.key}
              onClick={() => setPreAction(p => (p === opt.key ? null : opt.key))}
              style={{
                padding: '10px 16px', borderRadius: '18px', fontSize: '12px', fontWeight: 700,
                cursor: 'pointer', letterSpacing: '0.4px',
                border: preAction === opt.key ? '2px solid #22d3ee' : '1px solid rgba(255,255,255,0.25)',
                background: preAction === opt.key ? 'rgba(8, 145, 178, 0.9)' : 'rgba(15, 23, 42, 0.85)',
                color: preAction === opt.key ? '#fff' : '#cbd5e1',
                boxShadow: preAction === opt.key ? '0 0 12px rgba(34, 211, 238, 0.45)' : 'none',
              }}
            >
              {preAction === opt.key ? '✓ ' : ''}{opt.label}
            </button>
          ))}
        </div>
      )}

      {/* ACTION BUTTONS */}
      {canTakeAction && (
        <div className="premium-action-buttons">
          {!timeBankUsed && turnTimeLeft <= 15 && (
            <button className="premium-action-btn premium-timebank-btn" onClick={handleUseTimeBank} disabled={isProcessing}>+10s</button>
          )}
          {possibleActions.includes('check') && (
            <button className="premium-action-btn premium-check-btn" onClick={() => handlePlayerAction('check')} disabled={isProcessing}>CHECK</button>
          )}
          {possibleActions.includes('call') && (
            <button className="premium-action-btn premium-call-btn" onClick={() => handlePlayerAction('call')} disabled={isProcessing}>CALL {callAmount.toFixed(0)}</button>
          )}
          {possibleActions.includes('raise') && (
            <button className="premium-action-btn premium-raise-btn" onClick={() => {
              if (!showRaiseSlider && currentPlayer) {
                setRaiseAmount(minRaise);
              }
              setShowRaiseSlider(!showRaiseSlider);
            }} disabled={isProcessing}>RAISE</button>
          )}
          {possibleActions.includes('all-in') && (
            <button className="premium-action-btn premium-allin-btn" onClick={() => handlePlayerAction('all-in')} disabled={isProcessing}>ALL-IN {currentPlayer?.chips?.toFixed(0)}</button>
          )}
          {possibleActions.includes('fold') && (
            <button className="premium-action-btn premium-fold-btn" onClick={() => handlePlayerAction('fold')} disabled={isProcessing}>FOLD</button>
          )}
        </div>
      )}

      {/* Raise Slider - bet-sizing UI. Critical that this doesn't reset
          the user's chosen amount unexpectedly: blur no longer snaps to
          minRaise, presets are highlighted when active, and ALL-IN is a
          one-click commit. Turn timer freezes at 0 while this is open
          (see turn-timer effect) so the player can't get folded mid-pick. */}
      {showRaiseSlider && currentPlayer && (() => {
        const chips = currentPlayer.chips;
        const minR = Math.min(minRaise, chips);
        const bb = (gameState as any).bigBlind || (gameState as any).blinds?.bigBlind || 0;
        const isPreflop = round === 'preflop';
        const sizeOpts: Array<{ label: string; value: number; isAllIn?: boolean }> = [];
        sizeOpts.push({ label: 'Min', value: minR });
        // Preflop: BB multipliers (2x, 2.5x, 3x) when the action is on a
        // standard open and the user hasn't already raised. These compute as
        // raise-on-top values, capped at stack.
        if (isPreflop && bb > 0) {
          const callOnly = Math.max(0, maxBet - (currentPlayer.currentBet || 0));
          const v2 = Math.min(Math.max(minR, 2 * bb - callOnly), chips);
          const v25 = Math.min(Math.max(minR, Math.round(2.5 * bb) - callOnly), chips);
          const v3 = Math.min(Math.max(minR, 3 * bb - callOnly), chips);
          if (v2 < chips) sizeOpts.push({ label: '2× BB', value: v2 });
          if (v25 < chips && v25 !== v2) sizeOpts.push({ label: '2.5× BB', value: v25 });
          if (v3 < chips && v3 !== v25) sizeOpts.push({ label: '3× BB', value: v3 });
        }
        // Postflop pot fractions when there's a pot. Sizing counts the call:
        // a "½ pot" raise is half of (pot after you call) ON TOP of the call
        // - raw pot/2 under-sizes into an illegal raise when facing a bet.
        if (!isPreflop && pot > 0) {
          const callOnly = Math.max(0, maxBet - (currentPlayer.currentBet || 0));
          const potAfterCall = pot + callOnly;
          const third = Math.min(Math.max(minR, Math.round(potAfterCall / 3)), chips);
          const half = Math.min(Math.max(minR, Math.round(potAfterCall / 2)), chips);
          const twoThird = Math.min(Math.max(minR, Math.round(potAfterCall * 2 / 3)), chips);
          const full = Math.min(Math.max(minR, potAfterCall), chips);
          if (third < chips) sizeOpts.push({ label: '⅓ Pot', value: third });
          if (half < chips && half !== third) sizeOpts.push({ label: '½ Pot', value: half });
          if (twoThird < chips && twoThird !== half) sizeOpts.push({ label: '⅔ Pot', value: twoThird });
          if (full < chips && full !== twoThird) sizeOpts.push({ label: 'Pot', value: full });
        }
        sizeOpts.push({ label: 'ALL-IN', value: chips, isAllIn: true });

        const isAllIn = raiseAmount >= chips;
        const submitDisabled = isProcessing || raiseAmount <= 0 || (raiseAmount < minR && !isAllIn);

        // Step size for the +/- stepper: one big blind (min 1 chip). Matches
        // CoinPoker's stepper behavior so fine-tuning a size never requires
        // typing on mobile.
        const stepSize = Math.max(1, bb || 1);

        return (
          <div className="raise-slider-container">
            <div className="raise-slider-content">
              {/* Preset row first (CoinPoker order: sizes above the slider) */}
              <div className="raise-preset-buttons">
                {sizeOpts.map(opt => {
                  const active = Math.round(raiseAmount) === Math.round(opt.value);
                  return (
                    <button
                      key={opt.label}
                      className={`preset-btn${opt.isAllIn ? ' allin-preset' : ''}${active ? ' preset-active' : ''}`}
                      onClick={() => setRaiseAmount(opt.value)}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* Stepper + amount + slider on one row: [-] [amount] [+] [slider] */}
              <div className="raise-amount-input-row">
                <button
                  className="raise-step-btn"
                  aria-label="Decrease raise"
                  onClick={() => setRaiseAmount(Math.max(minR, raiseAmount - stepSize))}
                  disabled={raiseAmount <= minR}
                >
                  &minus;
                </button>
                <div className="raise-amount-wrap">
                  <span className="raise-dollar-sign">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={chips}
                    value={raiseAmount || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : Number(e.target.value);
                      setRaiseAmount(Math.max(0, Math.min(val, chips)));
                    }}
                    /* No onBlur reset - validation happens only at submit. */
                    placeholder={`Min: $${minR}`}
                    className="raise-amount-input"
                  />
                </div>
                <button
                  className="raise-step-btn"
                  aria-label="Increase raise"
                  onClick={() => setRaiseAmount(Math.min(chips, raiseAmount + stepSize))}
                  disabled={raiseAmount >= chips}
                >
                  +
                </button>
              </div>

              <input
                type="range"
                min={minR}
                max={chips}
                value={Math.max(minR, Math.min(raiseAmount, chips))}
                onChange={(e) => setRaiseAmount(Number(e.target.value))}
                className="raise-slider"
              />
              <div className="raise-actions">
                <button
                  className="raise-confirm-btn"
                  onClick={() => {
                    if (raiseAmount < minR && raiseAmount < chips) {
                      setActionError(`Minimum raise is $${minR.toFixed(2)}`);
                      return;
                    }
                    handlePlayerAction('raise');
                  }}
                  disabled={submitDisabled}
                >
                  {isAllIn
                    ? `ALL-IN $${chips.toFixed(2)}`
                    : `RAISE TO $${(maxBet + raiseAmount).toFixed(2)}`}
                </button>
                <button
                  className="raise-cancel-btn"
                  onClick={() => setShowRaiseSlider(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Action Error */}
      {actionError && <div className="premium-action-error">{actionError}</div>}

      {/* Waiting indicator replaced by global turn timer banner above the SVG */}

      {/* Winner announcement bar */}
      {round === 'ended' && winners && winners.length > 0 && !isRunoutInProgress && (
        <div className="premium-winner-bar">
          <span className="winner-trophy"></span>

          <span className="winner-text">
            {(() => {
              const primaryWinner = [...winners].sort((a, b) => (b.amount || 0) - (a.amount || 0))[0];
              const handName = primaryWinner.hand && primaryWinner.hand !== 'Fold Win' ? primaryWinner.hand : null;
              return `${getPlayerDisplayName(primaryWinner.playerId)} wins $${primaryWinner.amount.toFixed(2)}${handName ? ` with ${handName}` : ''}`;
            })()}
          </span>
          <span className="next-hand-timer">Next hand in {nextHandCountdown}s...</span>
        </div>
      )}

      {/*  Rabbit Hunt button - only shown to players who were dealt in,

          when the hand ended early (less than full board) and isn't run-it-twice */}
      {round === 'ended'
        && communityCards.length < 5
        && !gameState.runItTwiceActive
        && !!safePlayers.find(p => p?.id === currentUserId) && (
        <div className="premium-runout-wrap" style={{
          display: 'flex', justifyContent: 'center',
          padding: '4px 0 8px 0',
        }}>
          <button
            onClick={() => setShowRabbitModal(true)}
            style={{
              background: 'linear-gradient(135deg, #d4af37, #b8941f)',
              color: '#1a1a1a',
              border: 'none',
              borderRadius: 20,
              padding: '8px 18px',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.4)',
            }}
          >
             See Runout

          </button>
        </div>
      )}

      {/* Rebuy button */}
      {currentPlayer?.needsRebuy && !sessionMode && (
        <div className="premium-rebuy-container">
          <button onClick={() => setShowRebuyModal(true)} disabled={isRebuyProcessing} className="premium-rebuy-btn">
            {isRebuyProcessing ? '⏳ Processing...' : ' REBUY NOW'}

          </button>
        </div>
      )}

      {/* Rebuy Modal */}
      <BuyInModal
        sessionMode={sessionMode}
        isOpen={showRebuyModal}
        onClose={() => setShowRebuyModal(false)}
        onConfirm={handleRebuy}
        minBuyIn={gameState.bigBlind ? gameState.bigBlind * 20 : 20}
        maxBuyIn={gameState.bigBlind ? gameState.bigBlind * 100 : 100}
        currency={gameState.currency || 'playcoins'}
        userId={currentUserId}
      />

      {/*  Rabbit Hunt Modal */}

      <RabbitHuntModal
        open={showRabbitModal}
        onClose={() => setShowRabbitModal(false)}
        tableId={tableId}
        handNumber={gameState.handNumber || 0}
      />

      {/* 🃏 Deal animation - brief card fan that plays whenever the engine
          starts a new hand. Visual signal that cards are finalized so the
          player isn't surprised when their hole cards "appear". */}
      <DealAnimation handNumber={gameState.handNumber} suppressUntil={(gameState as any).handStartsAt} />

      {/* Winner Celebration */}
      <WinnerCelebration show={showCelebration} amount={celebrationAmount} currency={tableCurrency} onComplete={() => setShowCelebration(false)} />

      {/* Notification - the card positions itself (fixed, top-centre,
          safe-area aware); the old full-screen wrapper double-offset it. */}
      {showNotification && notificationData && (
        <GameResultNotification
          type={notificationData.type}
          title={notificationData.title}
          message={notificationData.message}
          handName={notificationData.handName}
          detailLines={(() => {
            const b = (gameState as any).sevenTwoBountyResult;
            const base = notificationData.detailLines || [];
            if (b && b.handNumber === gameState.handNumber) {
              return [...base, `7-2 Bounty! ${b.winnerName} collected ${tableCurrency === 'pkcash' ? '$' : ''}${formatChips(b.totalBounty || 0)}`];
            }
            return base;
          })()}
          onClose={() => setShowNotification(false)}
        />
      )}

      {/*  SHOW CARDS BUTTON (Fold Wins) */}

      {canShowCards && (
        <div className="show-cards-container">
          <button className="show-cards-button" onClick={handleShowCards}>
             SHOW CARDS ({showCardsTimer}s)

          </button>
        </div>
      )}

      {/* RUN IT MULTIPLE TIMES PICKER - non-blocking panel above the action
          bar, never a screen-covering modal: the table (and everyone's
          revealed cards) stays visible while players pick. Each all-in
          player picks a count; the hand runs the MINIMUM picked. */}
      {showRITModal && (() => {
        const offer: any = gameState.runItTwiceOffer || {};
        const myPick = offer.responses?.[currentUserId];
        const waitingForOthers = myPick !== undefined;
        return (
          <div className="rit-picker">
            {waitingForOthers ? (
              <div className="rit-picker-waiting">
                You picked {Number(myPick) === 1 ? 'run it once' : `${myPick} times`}.
                Waiting for the other player...
              </div>
            ) : (
              <>
                <div className="rit-picker-title">All in! Run the board how many times?</div>
                <div className="rit-picker-buttons">
                  <button className="rit-pick-btn" disabled={ritResponding} onClick={() => handleRITResponse(1)}>
                    Once
                  </button>
                  <button className="rit-pick-btn rit-pick-primary" disabled={ritResponding} onClick={() => handleRITResponse(2)}>
                    Twice
                  </button>
                  <button className="rit-pick-btn rit-pick-primary" disabled={ritResponding} onClick={() => handleRITResponse(3)}>
                    3 Times
                  </button>
                </div>
                <div className="rit-picker-note">The table runs the lowest number picked</div>
                {ritCountdown !== null && ritCountdown > 0 && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#fbbf24', fontWeight: 700 }}>
                    Runs once automatically in {ritCountdown}s
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/*  STRADDLE BUTTON */}

      {canStraddle && !straddlePosted && (
        <div className="straddle-container">
          <button 
            className="straddle-button" 
            onClick={handlePostStraddle}
            disabled={postingStraddle}
          >
             Post Straddle (${(gameState.bigBlind || 2) * Math.max(2, Number(specialRules?.straddleMin) || 2)})

          </button>
        </div>
      )}
      {straddlePosted && (
        <div className="straddle-posted-indicator">
           Straddle Posted!

        </div>
      )}

      {/*  BOMB POT ANNOUNCEMENT */}

      {showBombPotAnnouncement && (
        <div className="bomb-pot-announcement">
          <div className="bomb-pot-text"> BOMB POT! </div>

        </div>
      )}

      {/*  7-2 BOUNTY CELEBRATION */}

      {showSevenTwoBounty && (
        <div className="seven-two-bounty-overlay">
          <div className="seven-two-bounty">
            <div className="seven-two-cards">7 2</div>

            <div className="seven-two-winner">{sevenTwoBountyWinner}</div>
            <div className="seven-two-amount">+${sevenTwoBountyAmount} Bounty!</div>
          </div>
        </div>
      )}

      {/*  ROUND ANNOUNCEMENT */}

      {showRoundAnnouncement && (
        <div className="round-announcement-overlay">
          <div className="round-announcement-text">{roundAnnouncementText}</div>
        </div>
      )}

      {/* BOT CHAT TOAST - top-left corner, away from the dealer artwork.
          Previously this sat top-center with a downward arrow pointing at
          the dealer, which read as Sophia speaking even when the message
          came from another bot. A corner toast with the speaker name as the
          headline removes that ambiguity. */}
      {!isPortrait && botChatBubble && (
        <div
          style={{
            position: 'absolute',
            top: '64px',
            left: '16px',
            maxWidth: '300px',
            background: 'rgba(15, 23, 42, 0.96)',
            border: '1px solid rgba(245, 158, 11, 0.6)',
            borderLeft: '4px solid #f59e0b',
            borderRadius: '10px',
            padding: '10px 14px',
            zIndex: 300,
            animation: 'pssBubbleLife 4.5s ease forwards',
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.45)',
            cursor: 'pointer',
          }}
          onClick={() => setBotChatBubble(null)}
        >
          <div style={{
            fontSize: '11px',
            fontWeight: 700,
            color: '#f59e0b',
            marginBottom: '3px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>{botChatBubbleName} says</div>
          <div style={{
            fontSize: '13px',
            color: '#e2e8f0',
            lineHeight: '1.4',
            fontWeight: 500,
          }}>{botChatBubble}</div>
        </div>
      )}

      {/*  CHAT TOGGLE BUTTON */}

      <button
        className="chat-toggle-btn"
        onClick={() => {
          setIsChatOpen(!isChatOpen);
          if (!isChatOpen) setUnreadMessages(0);
        }}
        title="Table chat"
      >
        <MessageCircle size={20} />
        {unreadMessages > 0 && <span className="unread-badge">{unreadMessages}</span>}
      </button>

      {/*  CHAT PANEL - Matches Classic exactly */}

      <TableChat
        tableId={tableId}
        currentUserId={currentUserId}
        currentUserName={usernames[currentUserId] || 'Player'}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        onNewMessage={() => {
          if (!isChatOpen) {
            setUnreadMessages(prev => prev + 1);
          }
        }}
      />

      {/*  TABLE RULES BUTTON */}

      {specialRules && (
        <button
          className="prem-rules-info-btn"
          onClick={() => setShowRulesModal(true)}
          title="Table Rules & Hand Rankings"
          style={{
            position: 'fixed',
            bottom: '200px',
            right: '16px',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0D3847 0%, #1A2E3B 100%)',
            border: '2px solid #00D9D9',
            color: '#00D9D9',
            fontSize: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            boxShadow: '0 4px 15px rgba(0, 217, 217, 0.3)',
            transition: 'all 0.2s',
          }}
        >
          ℹ
        </button>
      )}

      {/*  INVITE PLAYERS BUTTON */}

      <button
        className="prem-invite-btn"
        onClick={() => setShowInviteModal(true)}
        title="Invite Friends"
        style={{
          position: 'fixed',
          top: '70px',
          right: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '10px 16px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          color: '#ffffff',
          border: 'none',
          borderRadius: '25px',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          zIndex: 100,
          boxShadow: '0 4px 15px rgba(59, 130, 246, 0.35)',
        }}
      >
        <Users size={14} style={{ marginRight: 4 }} /> Invite
        

      </button>

      {/*  SHARE WIN BUTTON */}

      {showShareWinButton && shareWinData && (
        <button 
          className="share-win-btn"
          onClick={() => setShowShareWinModal(true)}
        >
           Share Win

        </button>
      )}

      {/*  TABLE RULES MODAL */}

      {showRulesModal && (
        <TableRulesModal
          isOpen={showRulesModal}
          onClose={() => setShowRulesModal(false)}
          specialRules={specialRules}
          bigBlind={gameState.bigBlind}
        />
      )}

      {/*  INVITE PLAYERS MODAL - same self-contained UserSearch the classic
          table uses (its own header, close button, and overlay dismissal;
          the old custom wrapper here had an EMPTY close button - no exit). */}
      <UserSearch
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        currentUid={currentUserId}
        currentUserName={usernames[currentUserId] || 'Player'}
        tableId={tableId}
        tableName={tableName}
      />

      {/*  SHARE WIN MODAL */}

      {showShareWinModal && shareWinData && (
        <ShareWinModal
          isOpen={showShareWinModal}
          onClose={() => {
            setShowShareWinModal(false);
            setShowShareWinButton(false);
          }}
          potAmount={shareWinData.potAmount}
          handType={shareWinData.handType}
          wasShowdown={shareWinData.wasShowdown}
          tableId={tableId}
          tableName={tableName}
        />
      )}
    </div>
  );
};

export default PremiumThemedTableSVG;